/*
 * Берём один модуль напрямую, а не пакет целиком.
 *
 * Этот файл целиком уезжает в закладку, а закладка живёт в адресной строке
 * и ограничена по длине. Импорт из корня общего пакета втягивает в неё всё
 * подряд, вместе с проверками схем, и сборка вырастает вчетверо — за одну
 * функцию очистки названия. Тот же приём уже используется между расширением
 * и веб-частью.
 */
import {
  названиеСлужебнойСтраницы,
  названиеЭтоКусокЗаголовка,
  очиститьНазваниеТовара,
} from '../../../shared/src/product-title.js';
import {
  описаниеБезРазметки,
  описаниеДляПоисковика,
  описаниеНачинаетсяСМагазина,
  описаниеПересказываетНазвание,
  описаниеПеречисляетРазделыСайта,
  описаниеПроМагазин,
} from '../../../shared/src/product-description.js';
import { годноеФото, картинкаДляРепоста, починитьАдресИзРазметки } from '../../../shared/src/product-image.js';
import {
  адресГоворитЭтоЧтиво,
  адресПохожНаТовар as адресПохожНаТоваруОбщее,
  развернутьПереход,
} from '../../../shared/src/product-url.js';
import {
  звеноПроКошельковуюЦену,
  надписьНеОЦенеТовара,
  ПРОСЬБА_НАЗВАТЬ_СУММУ,
} from '../../../shared/src/price-text.js';
import { ничейнаяЦенаВРазметке, ПОЛЯ_АРТИКУЛА } from '../../../shared/src/product-offer.js';
import {
  предложенияРазновидностей,
  разновидностиГруппы,
  ценаПредложения,
} from '../../../shared/src/page-kind.js';
import { этоТипВещи } from '../../../shared/src/product-types.js';
import { разобратьРазметкуТерпимо } from '../../../shared/src/jsonld-lenient.js';
import { валютаПоДомену } from '../../../shared/src/currency-by-host.js';
import { БЛОЧНЫЕ_ТЕГИ, НЕВИДИМЫЕ_ТЕГИ } from '../../../shared/src/block-tags.js';
import { заголовокГоворитПроЗаслон } from '../../../shared/src/block-title.js';
import { раскрытьМнемоники } from '../../../shared/src/entities.js';
import { detectCurrency, parsePriceToMinor, первоеЧисло, ЗНАКИ_ВАЛЮТ } from '../../../shared/src/money.js';
/**
 * Сбор карточки товара со страницы, открытой в браузере человека.
 *
 * Это ядро закладки-кнопки. Оно решает задачу, которую сервер решить не может
 * в принципе: Ozon, DNS-Shop и Технопарк закрыты антиботом, и наш сервер до их
 * страниц не доходит. Браузер человека эту страницу уже открыл — законно,
 * своей обычной сессией, — и данные лежат прямо в документе.
 *
 * Порядок источников — от самого надёжного к самому приблизительному:
 *
 *   1. JSON-LD с типом Product. Магазины держат его ради поиска, поэтому он
 *      есть почти везде и содержит ровно то, что нужно: название, цену, фото.
 *   2. OpenGraph. Держится ради превью в мессенджерах, тоже почти везде.
 *   3. Микроразметка itemprop прямо в вёрстке.
 *   4. Встроенное состояние приложения магазина: у некоторых площадок карточка
 *      лежит в JSON внутри страницы, а в разметку попадает не всё.
 *   5. Последняя попытка по вёрстке: заголовок первого уровня и первое число,
 *      похожее на цену рядом со значком рубля.
 *
 * Каждый следующий источник только ДОПОЛНЯЕТ пустые поля, но не затирает
 * найденное раньше: разметка вернее догадки по вёрстке.
 *
 * Здесь намеренно нет ничего, что обходило бы защиту сайта. Мы читаем документ,
 * который браузер уже показал человеку, — и только его.
 */

/**
 * Валюты, которые умеет хранить приложение.
 *
 * Список продублирован здесь намеренно: этот файл целиком уезжает в закладку,
 * и тянуть ради одной строки общий пакет значило бы утяжелить её на пустом
 * месте. Расхождение ловит проверка рядом — она сверяет оба списка.
 */
export { очиститьНазваниеТовара } from '../../../shared/src/product-title.js';

export const ПОДДЕРЖИВАЕМЫЕ_ВАЛЮТЫ = ['RUB', 'USD', 'EUR', 'KZT', 'BYN', 'GEL', 'AMD', 'TRY', 'CNY', 'GBP'] as const;

export interface CardFromPage {
  url: string;
  title?: string;
  description?: string;
  /** Цена в копейках. */
  priceMinor?: number;
  currency?: string;
  imageUrl?: string;
  /** Откуда что взялось — нужно для отладки и для честного сообщения человеку. */
  sources: string[];
}

/** Собирает карточку из документа. Ничего не запрашивает по сети. */
export function collectCardFromPage(doc: Document, pageUrl: string): CardFromPage {
  const card: CardFromPage = { url: pageUrl, sources: [] };
  /** Отложенное на самый конец: картинка для репоста, если другой не найдётся. */
  const запас: { репост?: string } = {};

  собратьИзJsonLd(doc, card);
  собратьОписаниеИзРазметкиТовара(doc, card);
  /*
   * Микроразметка спрашивается РАНЬШЕ OpenGraph, и это не мелочь порядка.
   *
   * og:image — картинка про страницу, микроразметка внутри [itemtype=Product] —
   * картинка про вещь. Когда они расходятся, права вторая.
   *
   * Поймано на Московском доме книги: обложка размечена как
   * `<a itemprop="image" href="/image/book/868/w259/i868297.jpg">`, а в og:image
   * лежит /i/main-cover.jpg — полоса 600×189, нарисованная в фотошопе в 2018-м
   * и одинаковая на весь сайт. Сервер брал обложку, закладка — полосу, потому
   * что OpenGraph успевал раньше и занимал поле. Человек, добавивший книгу
   * из браузера, получал в вишлист вывеску магазина вместо книги.
   */
  собратьИзМикроразметки(doc, card);
  собратьИзOpenGraph(doc, card, запас);
  собратьИзВстроенногоJson(doc, card, pageUrl);
  собратьИзВёрстки(doc, card);
  собратьИзКартинок(doc, card);

  /*
   * Картинка «для репоста» ждала до самого конца — и дождалась, если больше
   * ничего не нашлось. Товар на ней есть, просто обрезанный под чужую ленту,
   * и пустое поле человеку хуже. Подробности замера — у `картинкаДляРепоста`.
   */
  if (!card.imageUrl && запас.репост) положить(card, 'imageUrl', запас.репост, 'OpenGraph');

  if (card.title) {
    // Правило одно на оба разбора и живёт в общем пакете: серверный конвейер
    // чистит название тем же кодом, иначе одно и то же желание выглядело бы
    // по-разному в зависимости от того, как его добавили.
    const h1 = doc.querySelector('h1')?.textContent ?? undefined;
    card.title = очиститьНазваниеТовара(card.title, h1).slice(0, 200);
  }
  if (card.description) {
    /*
     * Разметку из описания убираем: магазины кладут в это поле готовый кусок
     * HTML. У Зоозавра там «<p>Потребности стерилизованных…</p><p>ROYAL CANIN…»,
     * и без чистки абзацы склеивались в одно слово, а теги ехали в вишлист.
     */
    card.description = описаниеБезРазметки(card.description).slice(0, 500);
    if (!card.description) card.description = undefined;
  }
  if (card.description) {
    /*
     * Визитку магазина за описание товара не выдаём — правило общее с сервером
     * и живёт в общем пакете. Из корпуса: у Глобуса в описание уезжало
     * «Гипермаркеты «Глобус». Официальный сайт.» — про подарок это не говорит
     * ничего. Проверяем после чистки названия: решение зависит от того, назван
     * ли товар в самом описании.
     */
    if (описаниеПроМагазин(card.description, card.title, имяМагазинаСоСтраницы(doc, pageUrl))) {
      card.description = undefined;
    }
    /*
     * Развёрнутая визитка проскакивает мимо правила выше: оно судит в том
     * числе по длине, а «В каталогах FINN FLARE представлены продуманные
     * до мелочей модели курток, пуховиков…» — 175 знаков. Судим по началу:
     * текст, открывающийся словами о магазине, о магазине и рассказывает.
     */
    if (card.description && описаниеНачинаетсяСМагазина(card.description)) {
      card.description = undefined;
    }
    /*
     * Третья визитка — та, что не называет магазина вовсе, а перечисляет его
     * разделы. С живой страницы Wildberries: «Коллекции женской, мужской и
     * детской одежды, обуви… Информация о доставке и оплате. Таблицы
     * размеров, советы по уходу за вещами.» Ни слова о той ленте, ради
     * которой человек пришёл. Выбрасываем насовсем, а не отодвигаем: строка,
     * молчащая о вещи, хуже пустого поля — под ним хотя бы виден запасной
     * путь к тексту с самой страницы.
     */
    if (card.description && описаниеПеречисляетРазделыСайта(card.description)) {
      card.description = undefined;
    }
  }

  /*
   * Строка для поисковика уступает дорогу настоящему описанию, но не пустоте.
   *
   * «Купить Серебряное кольцо с фианитами kari jewelry 534087F0 по цене
   * 1140 ₽ в интернет-магазине kari» человеку не даёт ничего сверх названия,
   * а цена внутри неё застынет и через месяц соврёт. Но выбрасывать её
   * насовсем нельзя: замер по 45 карточкам показал, что у восьмерых взамен
   * не нашлось ничего. Поэтому ищем замену в вёрстке и меняем, только если
   * замена нашлась.
   */
  if (
    card.description &&
    (описаниеДляПоисковика(card.description) || описаниеПересказываетНазвание(card.description, card.title))
  ) {
    const настоящее = описаниеИзВёрстки(doc);
    const годится =
      настоящее &&
      !описаниеПроМагазин(настоящее, card.title, имяМагазинаСоСтраницы(doc, pageUrl)) &&
      !описаниеНачинаетсяСМагазина(настоящее) &&
      !описаниеДляПоисковика(настоящее) &&
      !описаниеПересказываетНазвание(настоящее, card.title) &&
      !описаниеПеречисляетРазделыСайта(настоящее);
    if (годится) card.description = настоящее;
  }

  /*
   * Описания в разметке нет — берём то, что написано на самой странице.
   *
   * Пробуем ПОСЛЕ выброшенной визитки, и это существенно: у Глобуса единственное
   * описание в разметке — «Гипермаркеты «Глобус». Официальный сайт.», мы его
   * справедливо выбрасываем, а на странице под заголовком «О товаре» написан
   * состав мороженого. Сработай запасной путь раньше, визитка так и осталась бы
   * первой найденной и заслонила бы настоящий текст.
   */
  if (!card.description) {
    const изВёрстки = описаниеИзВёрстки(doc);
    if (изВёрстки && !описаниеПроМагазин(изВёрстки, card.title, имяМагазинаСоСтраницы(doc, pageUrl))) {
      положить(card, 'description', изВёрстки, 'вёрстка');
    }
  }
  card.imageUrl = вПолныйАдрес(крупнееТотЖеСнимок(тотЖеСнимокПолнымАдресом(card.imageUrl, doc), doc), doc, pageUrl);

  /*
   * Цена есть, а валюты нет — берём домашнюю валюту магазина по его домену.
   *
   * Правило то же, что и на сервере, и живёт в общем пакете. Из корпуса:
   * L'Occitane, Mixit и Скифмьюзик пишут цену без знака рубля, и человек видел
   * сумму без валюты — а серверный разбор той же страницы валюту подставлял.
   * Это последняя догадка: знак рядом с числом вернее и всегда идёт первым.
   */
  if (card.priceMinor !== undefined && !card.currency) {
    try {
      const домашняя = валютаПоДомену(new URL(pageUrl).hostname);
      if (домашняя) положить(card, 'currency', домашняя, 'домен магазина');
    } catch {
      /* адрес не разбирается — валюту оставим пустой */
    }
  }
  return card;
}

/**
 * Полный адрес картинки вместо относительного.
 *
 * Магазины пишут адреса как придётся: «/linkpics/noutbuk-small.jpg» у КНС,
 * «//imo10.labirint.ru/books/1017125/cover.jpg» у Лабиринта, «enaza/19627196/
 * box.jpg» у Hobby Games. В браузере такой адрес работает — его достраивает
 * сам браузер, — а к нам он приезжает как есть, и в вишлисте вместо подарка
 * остаётся пустое место. Серверный разбор достраивает адреса давно; закладка
 * отдавала их сырыми.
 *
 * Опираемся на baseURI, а не только на адрес страницы: у части магазинов стоит
 * тег <base>, и относительные адреса считаются от него.
 */
/**
 * Тот же снимок, но полным адресом, если магазин назвал его где-то ещё.
 *
 * Относительный адрес в разметке товара считается не от страницы, а от места,
 * которое магазин имел в виду и нам не сказал. У Hobby Games в разметке стоит
 * `enaza/19627196/box.jpg`, а настоящий адрес — `/image/enaza/19627196/box.jpg`:
 * достроенная от страницы ссылка ведёт в никуда, и в вишлисте вместо коробки
 * с игрой остаётся пустое место.
 *
 * Спасает то, что тот же файл магазин обычно называет ещё раз — в og:image
 * или в twitter:image, и уже полным адресом. Ищем среди них тот, что кончается
 * на нашу относительную запись: это тот же снимок, только с верным началом.
 */
function тотЖеСнимокПолнымАдресом(адрес: string | undefined, doc: Document): string | undefined {
  const относительный = адрес?.trim();
  if (!относительный || /^(https?:|data:|\/\/)/i.test(относительный)) return адрес;

  const хвост = относительный.replace(/^\.?\//, '');
  const источники = [
    'meta[property="og:image"]',
    'meta[name="twitter:image"]',
    'meta[property="og:image:secure_url"]',
    'link[rel="image_src"]',
  ];
  for (const селектор of источники) {
    const узел = doc.querySelector(селектор);
    const полный = (узел?.getAttribute('content') ?? узел?.getAttribute('href') ?? '').trim();
    if (/^https?:\/\//i.test(полный) && полный.split('?')[0]?.endsWith(хвост)) return полный;
  }
  return адрес;
}

/**
 * Тот же снимок, но крупнее, если магазин назвал где-то ещё его больший размер.
 *
 * Магазины раздают одну фотографию несколькими размерами, а в разметке товара
 * указывают мелкий: у befree в schema.org стоит `/images/320/…`, а в og:image
 * тот же файл в `/images/1024/…`. Разметке мы верим больше — и правильно
 * делаем, — но в вишлисте от этого стояла размытая картинка вместо резкой.
 *
 * Признак «тот же снимок» строгий: адреса должны совпадать целиком, кроме
 * ровно одной группы цифр, и в другой она должна быть больше. Так пара
 * «320 → 1024» проходит, а два разных снимка — нет: у них различий больше.
 */
function крупнееТотЖеСнимок(адрес: string | undefined, doc: Document): string | undefined {
  const свой = адрес?.trim();
  if (!свой) return адрес;

  for (const селектор of ['meta[property="og:image"]', 'meta[name="twitter:image"]', 'link[rel="image_src"]']) {
    const узел = doc.querySelector(селектор);
    const другой = (узел?.getAttribute('content') ?? узел?.getAttribute('href') ?? '').trim();
    if (другой && крупнее(свой, другой)) return другой;
  }
  return адрес;
}

/** Различаются ли адреса ровно одной группой цифр, и больше ли она во втором. */
function крупнее(свой: string, другой: string): boolean {
  const разбить = (адрес: string): string[] => адрес.split(/(\d+)/);
  const мой = разбить(свой);
  const чужой = разбить(другой);
  if (мой.length !== чужой.length) return false;

  let различий = 0;
  let большеУНего = false;
  for (const [место, кусок] of мой.entries()) {
    const пара = чужой[место] ?? '';
    if (кусок === пара) continue;
    // Различаться может только группа цифр: буквенные куски обязаны совпасть.
    if (!/^\d+$/.test(кусок) || !/^\d+$/.test(пара)) return false;
    различий += 1;
    большеУНего = Number(пара) > Number(кусок);
  }
  return различий === 1 && большеУНего;
}

function вПолныйАдрес(адрес: string | undefined, doc: Document, pageUrl: string): string | undefined {
  /*
   * Адрес из разметки бывает сломан самим магазином: Zenden вкладывает
   * в og:image второй полный адрес, СтройДвор пишет «&amp;» вместо «&».
   * Обе беды выглядят как рабочая ссылка и не открываются нигде.
   */
  const очищено = адрес ? починитьАдресИзРазметки(адрес) : undefined;
  if (!очищено) return undefined;
  // data: оставляем как есть — это не ссылка, а сама картинка.
  if (/^data:/i.test(очищено)) return очищено;
  /*
   * Через URL прогоняем и уже полные адреса — ради одинакового вида.
   * Серверный разбор делает это всегда, и на этом два разбора расходились:
   * у Долины роз и Посуды Центр в og:image написано «https://dolinaroz.ru:443/…»,
   * сервер лишний порт убирал, закладка отдавала адрес как есть. Ссылка
   * работала, а карточки выглядели разными.
   */
  try {
    return new URL(очищено, doc.baseURI || pageUrl).toString();
  } catch {
    try {
      return new URL(очищено, pageUrl).toString();
    } catch {
      // Достроить не вышло — лучше отдать пусто, чем заведомо битую ссылку:
      // пустое поле человек заполнит сам, а сломанная картинка выглядит
      // поломкой приложения.
      return undefined;
    }
  }
}


/**
 * Разбор JSON-LD, терпимый к небрежности магазина.
 *
 * Здесь лежала своя, короткая копия правил: строгий разбор и замена управляющих
 * символов на пробел — лечение живого перевода строки внутри описания, улика
 * Bosco. Копия отстала ровно так же, как раньше отставал сервер: обёртку CDATA
 * в блочном комментарии сервер читать научился 09.08.2026 (улика skifmusic), а
 * закладка — нет, и дыра просто переехала на другую сторону. Правила теперь
 * одни на обе стороны, `packages/shared/src/jsonld-lenient.ts`.
 */
function разобратьРазметку(текст: string | null): unknown {
  return разобратьРазметкуТерпимо(текст);
}

/** Заполняет поле, только если оно ещё пустое. Первый источник вернее. */
function положить<K extends keyof CardFromPage>(
  card: CardFromPage,
  key: K,
  value: CardFromPage[K] | undefined | null,
  источник: string,
): void {
  if (value === undefined || value === null || value === '') return;
  if (card[key] !== undefined && card[key] !== null && card[key] !== '') return;
  /*
   * Название служебной страницы названием подарка не бывает.
   *
   * Из нарисованного корпуса: у Фотосклада и X-Com разметка объявляет названием
   * «Главная страница» и «Главная», хотя на самой странице крупно написано имя
   * раздела. Разметке мы верим больше вёрстки — и правильно делаем, — но эти
   * два слова верить не за что: желание «Главная страница» человеку бесполезно
   * при любой цене. Пропускаем и берём следующий источник.
   */
  if (key === 'title' && названиеСлужебнойСтраницы(String(value))) return;
  card[key] = value;
  if (!card.sources.includes(источник)) card.sources.push(источник);
}

/**
 * Цена в копейках из чего угодно: числа, строки «12 990 ₽», «12.990,50».
 * Возвращает undefined, если это не похоже на цену, — выдуманная цена хуже пустой.
 *
 * Разбор самой суммы отдан общему пакету, и это не косметика. Здесь лежала
 * своя копия правил, и она разошлась с серверной ровно там, где ошибку никто
 * не искал: последний разделитель считался дробным, только если после него
 * РОВНО ДВЕ цифры. А разряды в числах всегда по три, значит одна цифра после
 * точки — это всегда доли, и никогда не разряды.
 *
 * Чего это стоило: любая цена с одной цифрой после точки читалась в десять
 * раз больше. «72990.0» у Сулпака превращалось в 729 900 ₸, «1499.5» —
 * в 14 995. Серверный разбор те же строки читал верно, и одна и та же вещь
 * стоила по-разному в зависимости от того, добавили её ссылкой или закладкой.
 * Хуже всего, что закладка — единственный путь на айфоне.
 *
 * Проверено на всех трудных случаях корпуса: ценник Глобуса из двух кусков
 * («719» и «99»), точки как разряды («1.000.000»), запятая как дробная часть
 * («12.990,50»), цена рядом с бонусами — все читаются как прежде. Меняются
 * только те, где раньше был десятикратный промах.
 */
export function ценаВКопейки(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.round(raw * 100);
  if (typeof raw !== 'string') return undefined;

  /*
   * Первое число строки берём тем же правилом, что и серверный разбор.
   *
   * Склеивать все цифры подряд нельзя: у Глобуса ценник набран двумя кусками,
   * «719» и «99», и склеенные они давали 71 999 ₽ вместо 719. Пробел считается
   * разделителем разрядов, только если за ним ровно три цифры.
   */
  const число = parsePriceToMinor(первоеЧисло(raw));
  if (число === null || число === undefined) return undefined;
  if (число <= 0) return undefined;
  // Тот же потолок, что и у догадок на сервере: подарок дороже десяти миллионов
  // если и бывает, то не берётся угадыванием по странице.
  if (число > 10_000_000_00) return undefined;
  return число;
}

function собратьИзJsonLd(doc: Document, card: CardFromPage): void {
  const товары: Record<string, unknown>[] = [];

  const блоки = doc.querySelectorAll('script[type="application/ld+json"]');
  for (const блок of Array.from(блоки)) {
    let данные: unknown;
    try {
      данные = разобратьРазметку(блок.textContent);
      if (данные === undefined) continue;
    } catch {
      continue;
    }

    for (const узел of развернуть(данные)) {
      if (!узел || typeof узел !== 'object') continue;
      const объект = узел as Record<string, unknown>;
      // Книга, игра, фильм и квартира — такие же предметы вишлиста, как товар,
      // и размечают их своими типами. Список общий с серверным разбором.
      if (этоТипВещи(объект['@type'])) товары.push(объект);
    }
  }

  /*
   * Сначала тот товар, о котором страница, — а не тот, что встретился первым.
   *
   * Магазины размечают рядом с товаром его аналоги: у Регарда на странице
   * карты памяти Transcend 133x размечена ещё и 1000x. Пока брался первый
   * попавшийся, порядок в разметке решал, чьё название и чья цена уедут
   * в вишлист, — а порядок этот магазин выбирает не для нас.
   *
   * Сортировка устойчивая: у товаров с равным совпадением порядок в разметке
   * сохраняется, и на обычной карточке с единственным товаром ничего
   * не меняется.
   */
  const поУбываниюСовпадения = товары
    .map((объект, номер) => ({ объект, номер, доля: долиСовпадения(doc, asText(объект.name) ?? '') }))
    .sort((а, б) => б.доля - а.доля || а.номер - б.номер)
    .map((запись) => запись.объект);

  for (const объект of поУбываниюСовпадения) {
    {
      положить(card, 'title', asText(объект.name), 'разметка товара');
      положить(card, 'description', asText(объект.description), 'разметка товара');

      const изображение = Array.isArray(объект.image) ? объект.image[0] : объект.image;
      const адресКартинки = asText(изображение) ?? asText((изображение as Record<string, unknown>)?.url);
      if (адресКартинки && годноеФото(адресКартинки)) положить(card, 'imageUrl', адресКартинки, 'разметка товара');

      /*
       * У группы своей цены нет — она у разновидности.
       *
       * Nike размечает шорты `ProductGroup`, а 54,99 € и валюту EUR кладёт
       * внутрь `hasVariant`. Сюда закладка не спускалась, цену потом
       * подбирала из встроенного JSON — и приносила число БЕЗ ВАЛЮТЫ.
       * Человек получал желание за 54,99 чего-то. Правило общее с серверным
       * разбором и требует, чтобы группа сама назвала ось различий.
       */
      const свои = развернуть(объект.offers);
      const предложения = свои.length > 0 ? свои : предложенияРазновидностей(объект);
      for (const предложение of предложения) {
        if (!предложение || typeof предложение !== 'object') continue;
        const о = предложение as Record<string, unknown>;
        const уточнение = о.priceSpecification as Record<string, unknown> | undefined;

        /*
         * Цену ищем во всех местах, где её кладут по стандарту.
         *
         * `lowPrice` — это AggregateOffer: так размечают товар, у которого
         * несколько предложений, например разные размеры обуви. Без него
         * страница с честной разметкой оставалась без цены на ровном месте.
         */
        положить(
          card,
          'priceMinor',
          ценаВКопейки(о.price ?? уточнение?.price ?? о.lowPrice ?? о.highPrice),
          'разметка товара',
        );
        положить(card, 'currency', asText(о.priceCurrency ?? уточнение?.priceCurrency), 'разметка товара');
      }

      // Цена бывает и прямо на товаре, минуя offers — встречается реже, но встречается.
      положить(card, 'priceMinor', ценаВКопейки((объект as Record<string, unknown>).price), 'разметка товара');
    }
  }
}

function собратьИзOpenGraph(doc: Document, card: CardFromPage, запас: { репост?: string }): void {
  const мета = (имя: string): string | undefined =>
    doc.querySelector(`meta[property="${имя}"], meta[name="${имя}"]`)?.getAttribute('content') ?? undefined;

  положить(card, 'title', мета('og:title'), 'OpenGraph');
  положить(card, 'description', мета('og:description') ?? мета('description'), 'OpenGraph');
  const изOpenGraph = мета('og:image') ?? мета('og:image:secure_url');
  if (изOpenGraph && годноеФото(изOpenGraph)) {
    /*
     * Картинку для репоста поле не занимает: она ждёт конца сборки и достаётся
     * только тогда, когда своего снимка на странице не нашлось. Иначе на Авито,
     * где нет ни schema.org, ни микроразметки, OpenGraph успевал первым и
     * человек получал полосу 537×240 вместо кадра 639×480. Подробности замера —
     * у `картинкаДляРепоста` в общем пакете.
     */
    if (картинкаДляРепоста(изOpenGraph)) запас.репост ??= изOpenGraph;
    else положить(card, 'imageUrl', изOpenGraph, 'OpenGraph');
  }
  положить(card, 'priceMinor', ценаВКопейки(мета('product:price:amount') ?? мета('og:price:amount')), 'OpenGraph');
  положить(card, 'currency', мета('product:price:currency') ?? мета('og:price:currency'), 'OpenGraph');
}

/**
 * Как магазин называет сам себя.
 *
 * Нужно ровно для одного: понять, что слово в названии товара — это имя
 * магазина, а не примета вещи. Магазины ставят своё имя в название собственной
 * марки, и без этой поправки визитка «Гипермаркеты «Глобус». Официальный сайт.»
 * объявляла бы себя описанием «Мороженого сливочного Глобус».
 *
 * Три источника по убыванию точности. og:site_name — прямой ответ, но его
 * ставят не все (у того же Глобуса его нет). Хвост заголовка после разделителя —
 * туда магазины дописывают себя: «… | Сеть гипермаркетов Глобус». Домен —
 * последнее прибежище: латиницей он совпадёт не со всяким именем, зато есть
 * всегда.
 */
function имяМагазинаСоСтраницы(doc: Document, pageUrl: string): string {
  const объявленное = doc.querySelector('meta[property="og:site_name"]')?.getAttribute('content')?.trim();
  // У некоторых магазинов в og:site_name лежит не имя, а весь рекламный абзац.
  if (объявленное && объявленное.length <= 60) return объявленное;

  const хвостЗаголовка = (doc.querySelector('title')?.textContent ?? '')
    .split(/[|—–]/)
    .slice(1)
    .join(' ')
    .trim();
  if (хвостЗаголовка) return хвостЗаголовка;

  try {
    return new URL(pageUrl).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/**
 * Описание из микроразметки ТОВАРА — раньше, чем og:description.
 *
 * Отдельным шагом и до OpenGraph, потому что порядок здесь и есть старшинство:
 * `положить` оставляет первое найденное. og:description магазин пишет для
 * поисковой выдачи, и у большинства это рекламная обёртка — «Купите X
 * в интернет-магазине Y с доставкой». А itemprop="description" внутри scope
 * товара магазин объявил описанием ИМЕННО ЭТОГО товара.
 *
 * Из корпуса, Иль де Ботэ: в og — «Купите оригинальный SPF 50+…», в
 * микроразметке — настоящий рассказ про флюид-экран. Серверный разбор теперь
 * предпочитает второе, и этот шаг держит два разбора в согласии.
 *
 * Не нашлось в области — берём НИЧЕЙНОЕ описание, у которого объемлющей
 * области нет вовсе. По спецификации свойство принадлежит ближайшей
 * объемлющей области, а если её нет — оно про саму страницу, то есть про этот
 * товар. Именно так устроено объявление Авито: область Offer там на 106
 * знаков (цена, валюта, наличие), а рассказ продавца на 1677 знаков лежит
 * снаружи любой области, и оба разбора отдавали вместо него строку
 * og:description — «iPhone 13 Pro, 256 ГБ, 1 SIM: объявление о продаже
 * в Москве на Авито», дальше обрезанный хвост.
 *
 * Ничейное — не «любое по странице»: `своиУзлы(doc, …)` берёт только те, над
 * которыми области нет. Описание соседнего товара из блока «с этим покупают»
 * лежит внутри его собственной области и так не проходит — как и шесть
 * `itemprop="name"` того же Авито, принадлежащих крошкам и магазину.
 */
function собратьОписаниеИзРазметкиТовара(doc: Document, card: CardFromPage): void {
  const область = doc.querySelector('[itemscope][itemtype*="Product" i]');
  const узел = область?.querySelector('[itemprop="description"]') ?? своиУзлы(doc, '[itemprop="description"]')[0];
  if (!узел) return;

  положить(card, 'description', узел.getAttribute('content') ?? видимыйТекст(узел), 'разметка товара');
}

/**
 * Текст узла так, как его ВИДИТ человек: блоки разделены, скрытое отброшено.
 *
 * `textContent` склеивает строки — он не знает ни про `<br>`, ни про соседние
 * блоки. Описание объявления Авито размечено `<br>`, и человек получал
 * «Лeгкo кaк pаз-двa-три!-------📱 iРhоne 13 Рrо» одной строкой, а условия
 * внастык: «Все функции работают🟢Возможность оформления гарантии». Серверный
 * разбор такой обход давно имеет — этот держит два разбора в согласии.
 *
 * `innerText` не годится, хотя знает про переносы сам: в JSDOM, на котором
 * считается опись нарисованного корпуса, его нет вовсе, и проверка мерила бы
 * не то, что видит человек.
 *
 * Обход без рекурсии: вёрстку пишем не мы, и страница с двадцатью тысячами
 * уровней вложенности рекурсией валит весь разбор. Списки узлов общие
 * с серверным обходом — вопрос «виден ли здесь перенос» один на два разбора.
 */
const КОНЕЦ_БЛОКА = { nodeType: -1 } as unknown as Node;

function видимыйТекст(корень: Element): string {
  const куски: string[] = [];
  const стопка: Node[] = [корень];

  let пройдено = 0;
  while (стопка.length > 0 && пройдено < 500_000) {
    const узел = стопка.pop()!;
    пройдено += 1;

    if (узел === КОНЕЦ_БЛОКА) {
      куски.push(' ');
      continue;
    }
    if (узел.nodeType === 3) {
      куски.push(узел.nodeValue ?? '');
      continue;
    }
    if (узел.nodeType !== 1) continue;

    const имя = (узел as Element).tagName.toLowerCase();
    if (НЕВИДИМЫЕ_ТЕГИ.has(имя)) continue;

    const блочный = БЛОЧНЫЕ_ТЕГИ.has(имя);
    if (блочный) {
      куски.push(' ');
      // Метка кладётся ДО детей: стопка отдаёт её последней, то есть уже
      // после всего текста блока.
      стопка.push(КОНЕЦ_БЛОКА);
    }
    const дети = узел.childNodes;
    for (let i = дети.length - 1; i >= 0; i -= 1) стопка.push(дети[i]!);
  }

  return куски.join('');
}

/**
 * Свойство САМОЙ области, а не вложенного в неё чужого объекта.
 *
 * В микроразметке свойство принадлежит ближайшей объемлющей области — так
 * говорит спецификация, и так это читает поисковик. Мы же брали первое
 * подходящее по документу, и на живой странице это оказалось не мелочью.
 *
 * У Лабиринта внутри <article itemtype="Product"> лежит вложенная область
 * Organization со своим <meta itemprop="name" content="Лабиринт.ру">, и она
 * стоит РАНЬШЕ названия книги. Название книги «Читать вслух — Антонова,
 * Дашевская, Басова» подменялось именем магазина. Ровно так же под удар
 * попадают цена и картинка: у товара бывают вложены и продавец, и марка,
 * и отзывы — у каждого свои name, price и image.
 */
function своиУзлы(область: Element | Document, селектор: string): Element[] {
  /*
   * Документ отличаем по типу узла, а не через instanceof Element.
   *
   * Этот файл работает в трёх местах: в браузере у человека, в расширении
   * и в Node при пересчёте описи по корпусу. В Node глобального Element нет
   * вовсе, и проверка через instanceof валила весь разбор с ReferenceError.
   */
  const этоДокумент = область.nodeType === 9;

  return Array.from(область.querySelectorAll(селектор)).filter((узел) => {
    const ближайшаяОбласть = узел.parentElement?.closest('[itemscope]') ?? null;
    // Своё — то, у которого ближайшая объемлющая область и есть наша.
    return этоДокумент ? ближайшаяОбласть === null : ближайшаяОбласть === область;
  });
}

function своёСвойство(область: Element | Document, имя: string): Element | null {
  return своиУзлы(область, `[itemprop="${имя}"]`)[0] ?? null;
}

function собратьИзМикроразметки(doc: Document, card: CardFromPage): void {
  /*
   * Ищем только внутри карточки товара, а не по всей странице.
   *
   * На странице товара почти всегда есть блок «с этим покупают», и у соседних
   * товаров такая же разметка. Поиск по всему документу брал ПЕРВЫЙ попавшийся
   * узел: на живой странице Notik так приезжал заголовок «Аксессуары» вместо
   * названия товара. С ценой там обошлось случайно, и рассчитывать на это
   * нельзя: неверная цена хуже отсутствующей, человек на неё рассчитывает.
   */
  const область = doc.querySelector('[itemscope][itemtype*="Product" i]') ?? doc;

  const значение = (узел: Element | null): string | undefined =>
    узел ? (узел.getAttribute('content') ?? узел.textContent ?? undefined) : undefined;

  const свойство = (имя: string): string | undefined => значение(своёСвойство(область, имя));

  /**
   * Имя, размеченное куском собственного заголовка, читаем заголовком целиком.
   *
   * Правило и все оговорки к нему — в `названиеЭтоКусокЗаголовка` общего
   * пакета: серверный разбор берёт его оттуда же, иначе одна и та же вещь
   * называлась бы по-разному в зависимости от того, ссылкой её добавили или
   * закладкой. Здесь остаётся только вопрос к документу — лежит ли узел
   * внутри h1, — и ответ на него у DOM и у cheerio разный.
   */
  const названиеПоЗаголовку = (узел: Element | null): string | undefined => {
    const имя = значение(узел);
    if (!узел || !имя) return имя;
    const заголовок = узел.closest('h1');
    if (!заголовок || заголовок === узел) return имя;
    const целиком = заголовок.textContent ?? '';
    return названиеЭтоКусокЗаголовка(имя, целиком) ? целиком : имя;
  };

  /*
   * Цена — своё свойство ЛИБО свойство своего предложения.
   *
   * Цена почти всегда лежит на уровень глубже, во вложенной области offers:
   * `<div itemprop="offers" itemscope itemtype="Offer"><span itemprop="price">`.
   * По спецификации она принадлежит предложению, а не товару, и строгий поиск
   * «только своё» её терял. Замер это и показал: у КНС цена пропала вовсе,
   * у Иль де Ботэ вместо 3 640 ₽ приезжало 364 000, у Лабиринта вместо 351 ₽
   * — 351 701. Ошибка в цене хуже её отсутствия: человек на неё рассчитывает.
   *
   * Предложения берём тоже только свои: у соседнего товара из блока
   * «с этим покупают» предложение своё собственное.
   */
  const свойствоПредложения = (имя: string): string | undefined => {
    for (const предложение of своиУзлы(область, '[itemprop="offers"][itemscope]')) {
      const найдено = значение(своёСвойство(предложение, имя));
      if (найдено?.trim()) return найдено;
    }
    return undefined;
  };

  /*
   * Последняя попытка: предложение, у которого нет своего товара.
   *
   * Бывает, что область товара кончается на заголовке, а цена лежит в
   * СОСЕДНЕЙ области Offer — не вложенной и не помеченной `itemprop="offers"`.
   * Тогда оба поиска выше пусты: своего свойства нет, своего предложения нет.
   * Так размечен Максидом, и разметка от этого не разваливается — у
   * предложения хозяина нет, а товар на странице один.
   *
   * Осторожность вся в слове «ничейное» и в счёте товаров, и объяснена там,
   * где это правило живёт: `ничейноеПредложение` ниже. Спрашиваем ровно то же
   * место и тем же правилом, которым страница опознаётся карточкой, иначе
   * она снова окажется карточкой с пустой ценой.
   */
  const полеНичейного = (имя: string): string | undefined => {
    if (doc.querySelectorAll('[itemscope][itemtype*="Product" i]').length !== 1) return undefined;
    const предложение = ничейноеПредложение(doc);
    return предложение ? полеПредложения(предложение, имя) : undefined;
  };

  // Если карточку товара в разметке не нашли, цену отсюда не берём вовсе:
  // по всей странице она почти наверняка окажется чужой.
  const этоКарточка = область !== doc;

  /*
   * Название — только внутри карточки товара, ровно как цена ниже.
   *
   * Раньше имя бралось по всей странице, и это сходило с рук лишь потому,
   * что OpenGraph успевал занять поле раньше. Стоило спросить микроразметку
   * первой, как в названия поехало то, что лежит первым в документе:
   * «FINN FLARE» у Финн Флаэр, «Лабиринт.ру» у Лабиринта, «ЭКСМО» у Эксмо,
   * «Главная» у Альпины. Вне области Product разметка описывает не вещь,
   * а сайт — и доверять ей нельзя ни в названии, ни в цене.
   */
  if (этоКарточка) {
    положить(card, 'title', названиеПоЗаголовку(своёСвойство(область, 'name')), 'микроразметка');
    // lowPrice — тот же AggregateOffer, что и в JSON-LD: товар с несколькими
    // предложениями. Берём его, когда обычной цены нет.
    положить(
      card,
      'priceMinor',
      ценаВКопейки(
        свойствоПредложения('price') ??
          свойствоПредложения('lowPrice') ??
          свойство('price') ??
          свойство('lowPrice') ??
          полеНичейного('price') ??
          полеНичейного('lowPrice'),
      ),
      'микроразметка',
    );
    положить(
      card,
      'currency',
      свойствоПредложения('priceCurrency') ?? свойство('priceCurrency') ?? полеНичейного('priceCurrency'),
      'микроразметка',
    );
  }
  const изМикроразметки = картинкаИзМикроразметки(область) ?? свойство('image');
  if (изМикроразметки && годноеФото(изМикроразметки)) положить(card, 'imageUrl', изМикроразметки, 'микроразметка');
}

/**
 * Фотография из микроразметки, как бы магазин её ни записал.
 *
 * Простого `src` мало. У AMF картинка размечена отдельным объектом:
 * `<div itemprop="image" itemtype="ImageObject"><link itemprop="contentUrl"
 * href="..."></div>` — ни src, ни content там нет вовсе, и разбор уходил
 * ни с чем. В вишлист вместо букета уезжала полоска из шаблона магазина.
 */
function картинкаИзМикроразметки(область: Element | Document): string | undefined {
  const узел = область.querySelector('[itemprop="image"]');
  if (!узел) return undefined;

  const прямо =
    узел.getAttribute('content') ?? узел.getAttribute('src') ?? узел.getAttribute('href') ?? undefined;
  if (прямо?.trim()) return прямо.trim();

  const внутри = узел.querySelector('[itemprop="contentUrl"], [itemprop="url"]');
  const изВнутреннего =
    внутри?.getAttribute('href') ?? внутри?.getAttribute('content') ?? внутри?.getAttribute('src') ?? undefined;
  if (изВнутреннего?.trim()) return изВнутреннего.trim();

  const картинка = узел.querySelector('img');
  const изКартинки = картинка ? адресКартинки(картинка) : '';
  return изКартинки || undefined;
}

/*
 * Блоки состояния приложения — их разбираем настоящим JSON, а не текстом.
 *
 * Вынесены в постоянную, потому что спрашивают о них теперь двое: извлекатель
 * ниже и опознаватель в конце файла. Разъехавшись, эти двое читали бы одну
 * и ту же страницу по-разному — а именно от такого расхождения здесь уже
 * страдали.
 */
const БЛОКИ_СОСТОЯНИЯ = ['__NEXT_DATA__', '__NUXT_DATA__'];

/*
 * Цена и название в обычном скрипте страницы.
 *
 * Так их пишут в счётчиках: `dataLayer.product_page({ "name": "Коварный крот",
 * "price": 990 })`. Обе приметы — общие с серверным разбором (`extract/state.ts`,
 * `изОбычныхСкриптов`), и держать их в двух местах порознь нельзя: одна и та же
 * страница отдавала бы цену закладке и не отдавала серверу.
 */
const ЦЕНА_В_СКРИПТЕ = /"(?:finalPrice|cardPrice|salePrice|currentPrice|price)"\s*:\s*"?([\d\s.,\u00a0]+)"?/i;
const ИМЯ_В_СКРИПТЕ = /"(?:name|title|productName)"\s*:\s*"([^"]{4,200})"/i;

/** Скрипты страницы, где может лежать цена: без блоков состояния и без пустышек. */
function скриптыСЦеной(doc: Document): string[] {
  return Array.from(doc.querySelectorAll('script'))
    .filter((узел) => !БЛОКИ_СОСТОЯНИЯ.includes(узел.id))
    .map((узел) => узел.textContent ?? '')
    .filter((текст) => текст.length > 40 && текст.length < 3_000_000 && /"price"|"cardPrice"|"finalPrice"/i.test(текст));
}

/**
 * Встроенное состояние магазина.
 *
 * Некоторые площадки рисуют карточку скриптами, а в документ кладут JSON
 * с готовыми данными. Ключи у всех разные, поэтому ищем не по имени магазина,
 * а по смыслу: любое место, где рядом лежат название и цена.
 */
function собратьИзВстроенногоJson(doc: Document, card: CardFromPage, pageUrl: string): void {
  /*
   * Фотография — такой же повод сюда зайти, как название и цена.
   *
   * Раньше условие спрашивало только про них, и на магазинах, где вся страница
   * рисуется в браузере, разбор уходил отсюда с пустым фото. У Иль де Ботэ
   * на всей странице ровно один тег img — эмблема бренда 105×51, — а сам
   * флакон лежит в состоянии Next.js полем mainImage рядом с названием.
   * Название и цену страница отдавала разметкой, значит условие срабатывало,
   * и до состояния дело не доходило: в вишлисте оставалось пустое место.
   */
  if (card.title && card.priceMinor && card.imageUrl) return;

  /*
   * Сначала разбираем состояние приложения как настоящий JSON.
   *
   * Магазины на Next.js и Nuxt кладут в страницу всю карточку целиком, и это
   * самый точный источник после разметки: там лежит ровно то, что нарисовано
   * на экране. Регулярка по тексту такого не даёт — она цепляет первое
   * попавшееся поле «price», а им запросто окажется цена доставки.
   */
  for (const id of БЛОКИ_СОСТОЯНИЯ) {
    const блок = doc.getElementById(id);
    if (!блок?.textContent) continue;
    try {
      const данные = разобратьРазметку(блок.textContent);
      // Плоская запись Nuxt разбирается отдельно: в ней вместо значений стоят
      // номера мест, и обычный обход прочитал бы номер как сумму.
      if (Array.isArray(данные)) изПлоскогоСостояния(данные, card, doc, pageUrl);
      else обойтиСостояние(данные, card, doc, pageUrl);
    } catch {
      /* состояние не разобралось — ниже попробуем текстом */
    }
    if (card.title && card.priceMinor && card.imageUrl) return;
  }

  /*
   * Запасной путь по тексту: годится, когда состояние лежит не в отдельном
   * блоке, а прямо в скрипте.
   *
   * Блоки состояния сюда не пускаем. Они уже разобраны выше по-настоящему,
   * а поиск по тексту читает их неправильно: в плоской записи Nuxt поле
   * "price" содержит номер места в массиве, и у Mixit гель за 685 ₽ получал
   * цену 1492 ₽ — это оказался номер ячейки, в которой лежала стоимость
   * доставки.
   */
  for (const текст of скриптыСЦеной(doc)) {
    const цена = ЦЕНА_В_СКРИПТЕ.exec(текст);
    const имя = ИМЯ_В_СКРИПТЕ.exec(текст);
    положить(card, 'priceMinor', ценаВКопейки(цена?.[1]), 'встроенный JSON');
    положить(card, 'title', имя?.[1] ? раскодировать(имя[1]) : undefined, 'встроенный JSON');
    if (card.title && card.priceMinor) return;
  }
}

/**
 * Состояние Nuxt: плоский массив, внутри объектов вместо значений — номера мест.
 *
 * Выглядит это так: `{"name":1489,"price":1492}` означает не «цена 1492», а
 * «имя лежит в ячейке 1489, цена — в 1492». Прочитанное как обычный JSON, это
 * даёт правдоподобную и совершенно выдуманную сумму: у Mixit гель за 685 ₽
 * получал цену 1492 ₽, потому что в ячейке 1492 лежала стоимость доставки.
 *
 * Разбираем по правилам самого формата: значение поля — это ссылка, и брать
 * надо то, на что она указывает. Пара «имя и цена в одном объекте» остаётся
 * тем же условием, что и при обычном обходе.
 */
function изПлоскогоСостояния(массив: unknown[], card: CardFromPage, doc: Document, pageUrl: string): void {
  /*
   * Берём отсюда цену, только когда уже знаем, чему она принадлежит.
   *
   * Без имени товара в плоском состоянии не отличить его от услуги: у Mixit
   * первой парой «имя и цена» идёт способ доставки, и разбор наугад выдал бы
   * за цену геля 380 рублей курьера. Пустая цена честнее выдуманной — её
   * человек впишет сам, а неверную сначала примет за настоящую.
   */
  const искомое = ключевыеСлова(card.title);
  if (искомое.length === 0) return;

  const поСсылке = (значение: unknown): unknown =>
    typeof значение === 'number' && Number.isInteger(значение) && значение >= 0 && значение < массив.length
      ? массив[значение]
      : значение;

  for (const узел of массив) {
    if (!узел || typeof узел !== 'object' || Array.isArray(узел)) continue;
    const запись = узел as Record<string, unknown>;

    const имя = поСсылке(запись.name ?? запись.title ?? запись.productName);
    if (typeof имя !== 'string' || имя.trim().length < 4) continue;
    // Имя должно быть про тот же товар, что и заголовок страницы.
    const слова = new Set(ключевыеСлова(имя));
    const совпало = искомое.filter((слово) => слова.has(слово)).length;
    if (совпало / искомое.length < 0.6) continue;

    // Фотография — там же, у узла с подходящим именем, и по ссылке, как всё
    // остальное в плоской записи.
    if (card.imageUrl === undefined) {
      const снимок = фотоИзУзла(Object.fromEntries(Object.entries(запись).map(([к, з]) => [к, поСсылке(з)])));
      const адрес = вПолныйАдрес(снимок, doc, pageUrl);
      if (адрес && годноеФото(адрес)) положить(card, 'imageUrl', адрес, 'встроенный JSON');
    }

    const цена = поСсылке(запись.price ?? запись.finalPrice ?? запись.cardPrice ?? запись.salePrice);
    const минор = ценаВКопейки(typeof цена === 'number' || typeof цена === 'string' ? цена : undefined);
    if (минор === undefined) continue;

    положить(card, 'priceMinor', минор, 'встроенный JSON');
    const валюта = поСсылке(запись.currency ?? запись.priceCurrency);
    положить(card, 'currency', typeof валюта === 'string' ? валюта : undefined, 'встроенный JSON');
    if (card.imageUrl !== undefined) return;
  }
}

/** Слова названия, по которым его узнают: без предлогов, знаков и мелочи. */
function ключевыеСлова(текст: string | undefined): string[] {
  return (текст ?? '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .split(' ')
    .filter((слово) => слово.length >= 3);
}

/**
 * Ищет в состоянии узел, где рядом лежат название и цена.
 *
 * Именно «рядом»: одинокое поле price по всему дереву почти наверняка чужое —
 * доставка, товар из подборки, цена подписки. Пара «имя и цена в одном объекте»
 * куда надёжнее, и её достаточно, чтобы заполнить карточку.
 */
function обойтиСостояние(корень: unknown, card: CardFromPage, doc: Document, pageUrl: string, глубина = 0): void {
  if (глубина > 8 || корень === null || typeof корень !== 'object') return;

  if (Array.isArray(корень)) {
    for (const элемент of корень.slice(0, 200)) обойтиСостояние(элемент, card, doc, pageUrl, глубина + 1);
    return;
  }

  const узел = корень as Record<string, unknown>;
  const имя = узел.name ?? узел.title ?? узел.productName;
  const цена = узел.price ?? узел.finalPrice ?? узел.cardPrice ?? узел.salePrice ?? узел.currentPrice;

  /*
   * Фотографию берём только у того узла, который называется тем же товаром,
   * что и страница.
   *
   * В состоянии лежит вся страница разом: меню, подборка «с этим покупают»,
   * недавно просмотренное. Фотография из любого из них выглядит настоящей —
   * и человек заметит подмену только в вишлисте, когда вместо своего флакона
   * увидит чужую помаду. Цена и название так не проверялись, потому что их
   * узел выдаёт парой; у картинки такой пары нет, и порукой служит имя.
   */
  if (card.imageUrl === undefined && typeof имя === 'string' && имяПроТотЖеТовар(имя, card.title)) {
    const адрес = вПолныйАдрес(фотоИзУзла(узел), doc, pageUrl);
    if (адрес && годноеФото(адрес)) положить(card, 'imageUrl', адрес, 'встроенный JSON');
  }

  if (typeof имя === 'string' && имя.trim().length >= 4 && цена !== undefined && цена !== null) {
    const минор = ценаВКопейки(typeof цена === 'object' ? (цена as Record<string, unknown>).value : цена);
    if (минор !== undefined) {
      положить(card, 'title', имя.trim(), 'встроенный JSON');
      положить(card, 'priceMinor', минор, 'встроенный JSON');
      const валюта = узел.currency ?? узел.priceCurrency;
      положить(card, 'currency', typeof валюта === 'string' ? валюта : undefined, 'встроенный JSON');
      // Дальше вглубь идём только за фотографией: название и цену этот узел
      // уже отдал, а картинка бывает и ниже — в списке снимков товара.
      if (card.imageUrl !== undefined) return;
    }
  }

  for (const значение of Object.values(узел)) обойтиСостояние(значение, card, doc, pageUrl, глубина + 1);
}

/**
 * Адрес фотографии, как его пишут в состоянии приложения.
 *
 * Единого имени у поля нет: `mainImage` у Иль де Ботэ, `image` в разметке,
 * `imageUrl` и `picture` у других. Значением бывает и строка, и список
 * снимков, и объект с полем `url` — берём первое, что похоже на адрес.
 */
function фотоИзУзла(узел: Record<string, unknown>): string | undefined {
  for (const поле of ['mainImage', 'image', 'imageUrl', 'picture', 'photo', 'img', 'previewImage', 'images']) {
    const значение = узел[поле];
    const адрес = первыйАдрес(значение);
    if (адрес) return адрес;
  }
  return undefined;
}

function первыйАдрес(значение: unknown, глубина = 0): string | undefined {
  if (глубина > 2) return undefined;
  if (typeof значение === 'string') return значение.trim() || undefined;
  if (Array.isArray(значение)) {
    for (const элемент of значение.slice(0, 5)) {
      const адрес = первыйАдрес(элемент, глубина + 1);
      if (адрес) return адрес;
    }
    return undefined;
  }
  if (значение && typeof значение === 'object') {
    const запись = значение as Record<string, unknown>;
    return первыйАдрес(запись.url ?? запись.src ?? запись.large ?? запись.original, глубина + 1);
  }
  return undefined;
}

/**
 * Про тот же ли товар это имя, что и заголовок страницы.
 *
 * Считаем долю слов ИМЕНИ, найденных в заголовке, а не наоборот. Заголовок
 * страницы на этом шаге ещё не очищен от рекламы магазина, и в нём хватает
 * лишних слов: у Иль де Ботэ это «Оригинал 💯 … купить в интернет магазине
 * ИЛЬ ДЕ БОТЭ с доставкой.». Считая в обратную сторону, мы бы получили 0.59
 * при полном совпадении названия товара — и отказались от верной фотографии
 * из-за чужой рекламы.
 */
function имяПроТотЖеТовар(имя: string, заголовок: string | undefined): boolean {
  const слова = ключевыеСлова(имя);
  const изЗаголовка = new Set(ключевыеСлова(заголовок));
  if (слова.length === 0 || изЗаголовка.size === 0) return false;
  const совпало = слова.filter((слово) => изЗаголовка.has(слово)).length;
  return совпало / слова.length >= 0.6;
}

function собратьИзВёрстки(doc: Document, card: CardFromPage): void {
  положить(card, 'title', doc.querySelector('h1')?.textContent ?? undefined, 'вёрстка');

  /*
   * Заголовок окна — последнее прибежище названия, и на Wildberries
   * единственное.
   *
   * Стоит ПОСЛЕ h1 нарочно: заголовок окна магазины пишут для поисковика,
   * с ценой и своим именем в хвосте, и брать его вперёд разметки было бы
   * шагом назад. Но когда до него дошло, значит не нашлось ни разметки,
   * ни OpenGraph, ни h1 — и тогда он лучше пустоты.
   *
   * Поймано на живой странице Wildberries 07.08.2026, снятой настоящим окном
   * браузера. Магазин рисуется скриптами, и `og:title` у него один на весь
   * сайт — «Интернет‑магазин Wildberries: широкий ассортимент товаров».
   * Разбор его брал, и человек получал желание с вывеской вместо ленты
   * за 1630 ₽. Как только вывеску перестали брать, поле осталось ПУСТЫМ:
   * h1 на странице нет вовсе. А в заголовке окна название стоит целиком —
   * «Набор 4 шт Лента Металлизированная, 5 мм Веселая Затея 1149604851
   * купить за 1 630 ₽ в интернет‑магазине Wildberries», — и чистка названия
   * срезает с него и цену, и имя магазина.
   *
   * Серверный разбор эту страницу уже читал верно, своей эвристикой. То есть
   * шаг не добавляет новой догадки, а сводит два разбора к одному ответу —
   * ради чего общие правила и заведены.
   */
  /*
   * Заслон в заголовок окна попадает первым — и поймался на первом же пересчёте.
   *
   * У Pult.ru стоит `<title id="pageTitle">Проверка пользователя...</title>`,
   * и без этого условия новый источник принёс бы в желание именно её. До него
   * поле оставалось пустым — то есть шаг ухудшил бы карточку ровно там, где
   * магазин нас не пустил.
   *
   * Спрашиваем той же приметой, что и серверный разбор, но решаем мягче: там
   * по ней хоронят страницу целиком и потому требуют ещё признаков, а здесь мы
   * всего лишь отказываемся от кандидата и берём следующий источник.
   */
  const заголовокОкна = doc.querySelector('title')?.textContent ?? undefined;
  if (!заголовокГоворитПроЗаслон(заголовокОкна)) {
    положить(card, 'title', заголовокОкна, 'заголовок окна');
  }

  if (card.priceMinor !== undefined) return;
  const найдено = главнаяЦенаНаСтранице(doc);
  if (найдено) {
    положить(card, 'priceMinor', найдено.минор, 'вёрстка');
    /*
     * Валюту НЕ подставляем рублём наугад.
     *
     * Знак нашёлся, но приложение такую валюту не хранит — значит поле должно
     * остаться пустым, чтобы человек выбрал сам. Подставить рубль к цене
     * в гривнах хуже, чем не подставить ничего: сумма выглядит осмысленной
     * и никто не заметит подмены.
     */
    положить(card, 'currency', найдено.валюта, 'вёрстка');
  }
}

/*
 * Список знаков — общий с серверным разбором, из `shared/money.ts`.
 *
 * Свой собственный тут и стоял, и разошёлся с серверным в обе стороны: здесь
 * знали «₴», «тг» и «BYN», там — «₾», «₺» и «¥». Одна и та же страница
 * тбилисского магазина отдавала цену одному разбору и не отдавала другому.
 */

/**
 * Цена и следующий за ней знак валюты. Именно в таком порядке — так пишут у нас.
 *
 * Дробную часть забираем в то же число намеренно. Без неё разбор находил
 * не начало числа, а его хвост: в надписи «Цена за 1 кг — 959.99 руб.»
 * совпадало «99 руб», и мороженое за 719 ₽ получало цену 99 ₽. Число,
 * начатое посреди другого числа, — это не цена, а обрывок.
 */
const ЦЕНА_НА_СТРАНИЦЕ = new RegExp(`(\\d[\\d\\s\\u00a0\\u202f]{2,12}(?:[.,]\\d{1,2})?)\\s*(?:${ЗНАКИ_ВАЛЮТ})`, 'i');

/** Знак валюты ПЕРЕД числом — так пишут в зарубежных магазинах. */
const ЦЕНА_СО_ЗНАКОМ_ВПЕРЕДИ = /(?:\$|€|USD|EUR)\s*(\d[\d\s\u00a0\u202f.,]{2,12})/i;

/**
 * Валюта по знаку, найденному рядом с числом.
 *
 * Отдаём только то, что приложение умеет хранить. Гривну по знаку узнать
 * несложно, но в списке валют её нет — и карточка с ней не сохранилась бы
 * вовсе: человек заполнил бы форму, нажал «Добавить» и получил отказ,
 * не поняв, за что. Лучше вернуть пустую валюту и дать выбрать самому.
 */
function валютаПоЗнаку(текст: string): string | undefined {
  const найдено = распознатьЗнак(текст);
  return найдено && (ПОДДЕРЖИВАЕМЫЕ_ВАЛЮТЫ as readonly string[]).includes(найдено) ? найдено : undefined;
}

/*
 * Раньше здесь стоял свой разбор знака, и он молчал там, где не должен был.
 *
 * Во-первых, «\bр\.» не срабатывало ни разу: в JavaScript граница слова знает
 * только латиницу, и для неё кириллическая «р» — не буква. Цена «1 990 р.»
 * оставалась без валюты. Во-вторых, лари, драм, лиру, фунт и юань этот разбор
 * не знал вовсе, хотя приложение их хранит: карточка из Тбилиси приезжала
 * с суммой, но без валюты, и человек гадал, что это за число.
 *
 * Общий разбор из `shared/money.ts` знает и то, и другое — и, что важнее,
 * знает ровно то же, что серверный.
 */
function распознатьЗнак(текст: string): string | undefined {
  return detectCurrency(текст) ?? undefined;
}

/**
 * Главная цена страницы — та, которую человек считает ценой товара.
 *
 * Просто «первое число со знаком рубля» брать нельзя: выше по странице легко
 * попадается цена по карте магазина, стоимость доставки или баннер про скидку,
 * и в карточку уехала бы не та сумма. Неверная цена хуже отсутствующей: на неё
 * рассчитывают, собирая деньги на подарок.
 *
 * Поэтому выбираем так же, как выбирает глаз: самая крупная по начертанию
 * цена на видимой части страницы. У всех магазинов главная цена набрана
 * заметно крупнее сопутствующих — это свойство не вёрстки конкретного сайта,
 * а того, как люди читают страницы.
 *
 * Размер шрифта доступен только в настоящем браузере, а закладка именно в нём
 * и работает. Там, где его нет, честно откатываемся на порядок в документе.
 */
export function главнаяЦенаНаСтранице(doc: Document): { минор: number; валюта?: string } | undefined {
  const окно = doc.defaultView;
  let лучшая: { минор: number; валюта?: string; вес: number } | undefined;
  let порядок = 0;

  for (const узел of Array.from(doc.body?.querySelectorAll('*') ?? [])) {
    порядок += 1;

    /*
     * Содержимое скриптов человек не видит, а чисел там больше, чем на всей
     * остальной странице: настройки аналитики, состояние приложения, цены
     * чужих товаров. У Республики цена бралась прямо из window.__NUXT__.
     * Серверный разбор этот урок уже усвоил — здесь он повторялся.
     */
    if (/^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE)$/.test(узел.tagName)) continue;

    // Берём только собственный текст узла: иначе цена «всплывает» до самого
    // body, и крупнее всех оказывается контейнер, а не сама надпись.
    const свой = Array.from(узел.childNodes)
      .filter((дитя) => дитя.nodeType === 3)
      .map((дитя) => дитя.textContent ?? '')
      .join(' ')
      .trim();
    if (!свой) continue;

    const совпадение = ЦЕНА_НА_СТРАНИЦЕ.exec(свой) ?? ЦЕНА_СО_ЗНАКОМ_ВПЕРЕДИ.exec(свой);
    if (!совпадение) continue;

    /*
     * Надпись не о цене товара — пропускаем, какой бы крупной она ни была.
     *
     * Поймано на живой странице Zarina: поверх страницы висит плашка «Скидка
     * -15% от 5 000 руб. по промокоду ПРОМО15», набранная крупнее ценника.
     * Выбор по размеру шрифта честно брал её, и жакет за 3 999 ₽ уезжал
     * в вишлист за 5 000 ₽. Порог акции, начисление бонусов и платёж
     * в рассрочку — это суммы про что угодно, кроме стоимости вещи.
     *
     * Смотрим ТОЛЬКО рядом с числом, а не по всей надписи. Проверка по всему
     * тексту сразу же стоила настоящей карточки: у Республики цена написана
     * внутри длинного описания книги, и слово «доставка» где-то в его конце
     * вычёркивало цену, к которой не имело никакого отношения.
     */
    const начало = Math.max(0, совпадение.index - 40);
    const рядом = свой.slice(начало, совпадение.index + совпадение[0].length + 40);
    if (надписьНеОЦенеТовара(рядом + именаКлассов(узел))) continue;

    const минор = ценаВКопейки(совпадение[1]);
    if (минор === undefined) continue;

    // Старая цена зачёркнута — её показывают рядом с новой, и по начертанию
    // она бывает даже крупнее. Взять её значит завысить стоимость подарка.
    if (зачёркнута(узел as Element, окно)) continue;

    // Цена с кошельком площадки набрана крупнее обычной — и выиграла бы.
    if (ценаТолькоСКошельком(узел as Element)) continue;

    let вес = 0;
    try {
      const стиль = окно?.getComputedStyle(узел as Element);
      вес = Number.parseFloat(стиль?.fontSize ?? '') || 0;
      if (стиль && (стиль.display === 'none' || стиль.visibility === 'hidden')) continue;
    } catch {
      /* окна нет — останемся с весом 0 и решим порядком в документе */
    }

    const текущий = вес * 1000 - порядок;
    if (!лучшая || текущий > лучшая.вес) {
      лучшая = { минор, валюта: валютаПоЗнаку(свой), вес: текущий };
    }
  }

  return лучшая ? { минор: лучшая.минор, валюта: лучшая.валюта } : ценаПоИмениУзла(doc);
}

/**
 * Цена из узла, который магазин сам назвал ценой.
 *
 * Запасной ход для страниц, где знака валюты рядом с числом нет вовсе: у Глобуса
 * ценник набран двумя кусками — «719» и «99» в отдельных span, а рубль нарисован
 * значком. Собственный текст такого узла — просто число, и разбор по знаку его
 * не видит; зато имя класса говорит прямо: catalog-detail__item-price-actual.
 *
 * Идём сюда только когда по знаку не нашлось ничего: имя класса — довод слабее
 * знака валюты, рядом с ним запросто окажется цена доставки или соседнего
 * товара, и предпочитать его настоящему ценнику нельзя.
 */
function ценаПоИмениУзла(doc: Document): { минор: number; валюта?: string } | undefined {
  const окно = doc.defaultView;
  let лучшая: { минор: number; валюта?: string; вес: number } | undefined;
  let порядок = 0;

  for (const узел of Array.from(doc.querySelectorAll('[class*="price" i], [id*="price" i], [itemprop="price"]'))) {
    порядок += 1;
    if (узел.children.length > 3) continue;

    const текст = (узел.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!текст || текст.length > 60) continue;
    if (надписьНеОЦенеТовара(текст + именаКлассов(узел))) continue;
    if (зачёркнута(узел, окно)) continue;
    if (ценаТолькоСКошельком(узел)) continue;

    const минор = ценаВКопейки(текст);
    if (минор === undefined) continue;

    let вес = 0;
    try {
      const стиль = окно?.getComputedStyle(узел);
      вес = Number.parseFloat(стиль?.fontSize ?? '') || 0;
      if (стиль && (стиль.display === 'none' || стиль.visibility === 'hidden')) continue;
    } catch {
      /* окна нет — решим порядком в документе */
    }

    const текущий = вес * 1000 - порядок;
    if (!лучшая || текущий > лучшая.вес) лучшая = { минор, валюта: валютаПоЗнаку(текст), вес: текущий };
  }

  return лучшая ? { минор: лучшая.минор, валюта: лучшая.валюта } : undefined;
}

/**
 * Имена классов самой надписи и двух её обёрток.
 *
 * Слова о том, что это не цена товара, стоят не всегда рядом с числом. У Риглы
 * виджет Яндекс Сплита набирает «150 000 ₽» отдельным узлом, а слова «Лимиты
 * до» лежат в соседнем — по тексту эта сумма выглядит обычной ценой, и она
 * самая крупная на странице. Зато имена классов у неё говорящие:
 * `yandex-split__condition-text`.
 *
 * Глубже двух уровней не поднимаемся: там начинаются общие обёртки страницы,
 * и случайное слово в имени обёртки вычеркнуло бы настоящую цену.
 */
/**
 * Ценник, который показывают только при оплате кошельком площадки.
 *
 * Поднимаемся выше, чем `именаКлассов`: у Wildberries само число лежит в `h2`
 * без единого класса про кошелёк, а говорящее имя (`priceBlockWalletPrice`)
 * висит на кнопке через три уровня. Общий подъём на такую глубину опасен —
 * там уже общие обёртки страницы, — но эта примета узкая, случайно её не
 * встретишь: по обоим корпусам она нашлась ровно на одной странице.
 */
function ценаТолькоСКошельком(узел: Element): boolean {
  let текущий: Element | null = узел;
  for (let шаг = 0; шаг < 6 && текущий; шаг += 1) {
    const звено = звеноПроКошельковуюЦену(`${текущий.className ?? ''}`);
    if (звено !== 'дальше') return звено === 'кошелёк';
    текущий = текущий.parentElement;
  }
  return false;
}

function именаКлассов(узел: Element): string {
  const части: string[] = [];
  let текущий: Element | null = узел;
  for (let шаг = 0; шаг < 3 && текущий; шаг += 1) {
    части.push(`${текущий.className ?? ''}`);
    текущий = текущий.parentElement;
  }
  return ` ${части.join(' ')}`;
}

/**
 * Зачёркнута ли надпись — то есть это старая цена, а не текущая.
 *
 * Смотрим и на начертание, и на имена классов: часть магазинов рисует
 * зачёркивание картинкой или псевдоэлементом, и computed style об этом
 * молчит, а класс вида `price-old` остаётся.
 */
function зачёркнута(узел: Element, окно: Window | null): boolean {
  const имена = `${узел.className ?? ''} ${узел.getAttribute?.('data-testid') ?? ''}`.toLowerCase();
  if (/(^|[\s_-])(old|was|strike|crossed|discount-old|previous)([\s_-]|$)/.test(имена)) return true;
  if (/старая|зачерк/i.test(имена)) return true;

  try {
    const стиль = окно?.getComputedStyle(узел);
    if (стиль && /line-through/.test(стиль.textDecorationLine || стиль.textDecoration || '')) return true;
  } catch {
    /* стиля нет — судим только по именам */
  }

  // Родитель тоже считается: зачёркивают обычно обёртку, а не саму цифру.
  const родитель = узел.parentElement;
  if (родитель) {
    const именаРодителя = `${родитель.className ?? ''}`.toLowerCase();
    if (/(^|[\s_-])(old|was|strike|crossed)([\s_-]|$)/.test(именаРодителя)) return true;
    try {
      const стиль = окно?.getComputedStyle(родитель);
      if (стиль && /line-through/.test(стиль.textDecorationLine || стиль.textDecoration || '')) return true;
    } catch {
      /* и здесь обойдёмся именами */
    }
  }

  return false;
}

/*
 * Правило «годится ли адрес на роль фотографии» — общее с серверным разбором
 * и живёт в общем пакете. Пока правил было два, они разошлись: у Petshop
 * магазин объявил своей фотографией товара `templates/ps/img/logo.png` —
 * закладка эмблему отбрасывала, сервер брал.
 */

/**
 * Настоящий адрес картинки, даже если он спрятан ради ленивой загрузки.
 *
 * Магазины подставляют в `src` заглушку, а настоящий адрес кладут в data-атрибут
 * или в srcset — иначе браузер скачает всё сразу. Пока читался только `src`,
 * на таких страницах побеждала первая попавшаяся картинка оформления: у AMF
 * в вишлист уезжала полоска из шаблона вместо букета. Серверный разбор этот
 * список знает давно.
 */
function адресКартинки(img: Element): string {
  for (const атрибут of ['src', 'data-src', 'data-original', 'data-lazy-src', 'data-lazy', 'data-image', 'data-large-image']) {
    const значение = img.getAttribute(атрибут)?.trim();
    if (значение && !значение.startsWith('data:')) return значение;
  }
  return крупнейшийИзНабора(img.getAttribute('srcset') ?? img.getAttribute('data-srcset')) ?? '';
}

/**
 * Самый крупный вариант из srcset — по дескриптору, а не по месту в строке.
 *
 * Здесь брался последний кусок, и опиралось это на веру, что крупный вариант
 * магазин ставит в конец. Порядок в srcset ничем не задан: «1200w» первым
 * пишут ровно так же часто — так делают, когда крупный вариант считают
 * основным. На таких страницах в вишлист уезжал самый мелкий снимок.
 *
 * Правило взято у серверного разбора дословно (`pickLargestFromSrcset`
 * в scraper/url.ts), вплоть до веса плотности: «3x» — это тысячи, и оно
 * обгоняет любую ширину. Иначе одна и та же вещь получала бы резкую
 * фотографию, добавленная ссылкой, и размытую — добавленная закладкой.
 * Свой код, а не общий пакет, потому что этот файл целиком уезжает
 * в адресную строку закладки и лишний импорт ей дорог.
 */
function крупнейшийИзНабора(набор: string | null): string | undefined {
  let лучший: { адрес: string; вес: number } | undefined;

  for (const кусок of (набор ?? '').split(',')) {
    const части = кусок.trim().split(/\s+/);
    const адрес = части[0] ?? '';
    if (!адрес || адрес.startsWith('data:')) continue;

    const дескриптор = части[1] ?? '';
    const ширина = /^(\d+(?:\.\d+)?)w$/.exec(дескриптор);
    const плотность = /^(\d+(?:\.\d+)?)x$/.exec(дескриптор);
    // Без дескриптора сравнивать нечем — вес единичный, и побеждает первый.
    const вес = ширина?.[1] ? Number(ширина[1]) : плотность?.[1] ? Number(плотность[1]) * 1000 : 1;

    if (!лучший || вес > лучший.вес) лучший = { адрес, вес };
  }

  return лучший?.адрес;
}


/**
 * Последний рубеж по фотографии: самая крупная картинка на странице.
 *
 * Нужен там, где у магазина нет ни og:image, ни разметки картинки, — а такие
 * встречаются: X-Com отдавал название и цену, а фотографию нет. Желание без
 * картинки выглядит незаконченным, и человек лезет искать её руками.
 *
 * Выбираем по заявленному размеру, а не по порядку: первой на странице почти
 * всегда идёт эмблема магазина. Мелочь и служебные значки отбрасываем по тем же
 * признакам, по которым их отличает глаз — размер и слова «logo», «icon»
 * в адресе или подписи.
 */
function собратьИзКартинок(doc: Document, card: CardFromPage): void {
  if (card.imageUrl) return;

  let лучшая: { адрес: string; вес: number } | undefined;

  for (const img of Array.from(doc.querySelectorAll('img'))) {
    const адрес = адресКартинки(img);
    if (!адрес || адрес.startsWith('data:')) continue;
    if (!годноеФото(адрес)) continue;

    /*
     * Картинки из <noscript> человек не видит вовсе.
     *
     * Там живут счётчики посещаемости: у L'Occitane единственной картинкой
     * без служебных слов в адресе оказался пиксель Яндекс.Метрики
     * mc.yandex.ru/watch/10543693 — он и уезжал в вишлист вместо крема.
     */
    if (img.closest('noscript')) continue;

    const подпись = `${адрес} ${img.getAttribute('alt') ?? ''} ${img.className ?? ''}`.toLowerCase();
    if (/logo|icon|sprite|placeholder|banner|pixel|badge|payment|\.svg($|\?)/.test(подпись)) continue;

    /*
     * Плитка раздела — не фотография товара, даже если она самая крупная.
     *
     * У L'Occitane разметки картинки нет вовсе, и последним доводом остаётся
     * самая крупная картинка страницы. Ею оказывалась плитка блока
     * «Бестселлеры» с классом `category-card__img`, а настоящий крем лежал
     * рядом с классом `p-card__img`. В вишлист уезжала витрина категории
     * вместо баночки.
     *
     * Смотрим ТОЛЬКО на класс, а не на адрес: часть магазинов раскладывает
     * фотографии товаров по папкам категорий, и запрет по адресу отнял бы
     * у них настоящие снимки.
     */
    if (/categor|kategor/i.test(img.className ?? '')) continue;

    const ширина = Number.parseInt(img.getAttribute('width') ?? '', 10) || 0;
    const высота = Number.parseInt(img.getAttribute('height') ?? '', 10) || 0;

    // Заявленный размер есть не всегда. Когда его нет, полагаемся на порядок:
    // фотография товара идёт раньше блока «с этим покупают».
    const вес = ширина * высота || 1;
    if (ширина && высота && (ширина < 120 || высота < 120)) continue;

    if (!лучшая || вес > лучшая.вес) лучшая = { адрес, вес };
  }

  if (лучшая) положить(card, 'imageUrl', лучшая.адрес, 'картинки страницы');
}

function развернуть(значение: unknown): unknown[] {
  if (значение === undefined || значение === null) return [];
  if (Array.isArray(значение)) return значение.flatMap((элемент) => развернуть(элемент));
  if (typeof значение === 'object') {
    const граф = (значение as Record<string, unknown>)['@graph'];
    if (Array.isArray(граф)) return [значение, ...граф.flatMap((элемент) => развернуть(элемент))];
  }
  return [значение];
}

/*
 * Всё, что приходит сюда, вынуто из текста `<script>` — а это не HTML, и
 * сущности в нём браузер не раскрывает. Раскрываем сами, и здесь, в одном
 * месте на всю разметку товара: имя, описание, адрес картинки.
 *
 * Замер 08.08.2026, Котофото: в JSON-LD имя ноутбука записано «…16Gb 17&quot;
 * IPS QHD+…», в og:title той же страницы — уже с настоящей кавычкой. Разметка
 * товара старше OpenGraph, и человеку, добавившему желание закладкой, уезжало
 * `17&quot;` буквами; серверный разбор ту же страницу показывал верно. Одно
 * и то же желание выглядело по-разному в зависимости от того, чем его
 * добавили, — а это худший род расхождения: воспроизводится не у всех.
 *
 * Держится на этом одно только ИМЯ, и это стоит знать, правя код: у описания
 * расшифровка своя, внутри `описаниеБезРазметки`, а у адреса картинки — своя,
 * в `product-image.ts` (`?w=800&amp;h=600` завела не эта страница, а СтройДвор).
 * Здесь они сходятся в одном месте на всю разметку товара, но убери его — и
 * покраснеет имя, а описание с картинкой останутся целы.
 */
function asText(значение: unknown): string | undefined {
  if (typeof значение === 'string' && значение.trim()) return раскрытьМнемоники(значение.trim());
  return undefined;
}

function раскодировать(значение: string): string {
  // Два разных кодирования подряд, и порядок именно такой: сперва экранирование
  // JSON (`"`, `\/`), потом мнемоники HTML. Обратный порядок ничего
  // не чинит: `&quot;` до разбора JSON мнемоникой ещё не выглядит.
  try {
    return раскрытьМнемоники(JSON.parse(`"${значение.replace(/"/g, '\\"')}"`) as string);
  } catch {
    return раскрытьМнемоники(значение);
  }
}

/**
 * Название, пришедшее из системного «Поделиться».
 *
 * Телефон отдаёт заголовок открытой страницы — а это почти всегда название
 * товара с хвостом магазина: «Кроссовки Nike — купить в Ozon». Хвост режем,
 * заглушки антибота отбрасываем целиком: лучше пустое поле, чем «Ваши действия
 * признаны автоматическими» в качестве названия подарка.
 *
 * Сервер к этому моменту ещё ничего не знает: заголовок пришёл с телефона
 * человека, а не со страницы, которую мы прочитали. Для закрытых площадок это
 * единственное название, которое вообще можно получить без установки чего-либо.
 */
/**
 * Достаёт ссылку из того, чем поделились.
 *
 * Приложения ведут себя по-разному: одни кладут чистую ссылку в `url`, другие —
 * в `text` целую фразу вида «Смотри что нашёл: https://...». Берём первое, что
 * похоже на настоящий адрес.
 *
 * Живёт рядом с `названиеИзПоделиться` не случайно: это две половины одного
 * умения — разобрать строку, которую отдала кнопка «Поделиться». Нужны обе
 * и приёмнику системных ссылок, и форме, куда ту же строку вставляют руками.
 */
export function pickSharedUrl(input: {
  url?: string | null;
  text?: string | null;
  title?: string | null;
}): string | null {
  /*
   * Ссылку-перевалку разворачиваем сразу: настоящий адрес лежит у неё
   * в параметре. Человек, поделившийся товаром из ленты ВКонтакте, приносит
   * `vk.com/away.php?to=…`, и без этого шага разбор идёт на страницу
   * «Вы покидаете ВКонтакте» — ни названия, ни цены, ни фотографии.
   */
  const direct = input.url?.trim();
  if (direct && /^https?:\/\/\S+$/i.test(direct)) return развернутьПереход(direct);

  // Ссылка внутри текста — берём последнюю (обычно она в конце сообщения).
  for (const field of [input.text, input.title, input.url]) {
    if (!field) continue;
    const matches = field.match(/https?:\/\/[^\s<>"']+/gi);
    if (!matches || matches.length === 0) continue;
    /*
     * Ссылок в сообщении бывает две, и вторая — не товар.
     *
     * Приложения магазинов дописывают к тому, чем поделились, приглашение
     * себя установить: «Чайник Bosch https://shop.ru/p/1 Скачать приложение:
     * https://apps.apple.com/…». Правило «берём последнюю» отдавало магазин
     * из App Store, и человек получал желание «приложение Ozon» вместо чайника
     * — а сообразить, что произошло, было невозможно.
     */
    const развёрнутые = matches.map((адрес) => развернутьПереход(адрес));
    const товарные = развёрнутые.filter((адрес) => !НЕ_ТОВАРНАЯ_ССЫЛКА.test(адрес));
    const выбор = товарные.length > 0 ? товарные : развёрнутые;
    return выбор[выбор.length - 1]!;
  }

  return null;
}

/** Строка, в которой нет ничего, кроме суммы: у Авито это отдельная строка. */
const ТОЛЬКО_ЦЕНА = new RegExp(`^[\\d\\s\\u00a0\\u202f]{1,15}(?:[.,]\\d{1,2})?\\s*(?:${ЗНАКИ_ВАЛЮТ})\\.?$`, 'i');

/**
 * Строка про рассрочку: «от 3 500 ₽/мес».
 *
 * Названием она не бывает никогда, а к названию приклеивалась: магазины
 * ставят её отдельной строкой сразу под товаром, и в поле «Что это» уезжало
 * «Ноутбук ASUS от 3 500 ₽/мес». Ценой её тоже считать нельзя — это платёж,
 * а не стоимость вещи, и человек, увидев в вишлисте 3 500 вместо 84 000,
 * ошибётся куда дороже.
 */
const СТРОКА_РАССРОЧКИ = new RegExp(
  `^\\s*(?:от|рассрочка|в\\s+рассрочку)?[\\s\\d\\u00a0\\u202f.,]{1,15}(?:${ЗНАКИ_ВАЛЮТ})\\s*[/×x]\\s*(?:мес|месяц|мес\\.)`,
  'iu',
);

/**
 * Строка про начисление бонусов: «+2400 бонусов», «+240 баллов».
 *
 * Мегамаркет и Озон делятся отдельной строкой про бонусы, и без чистки она
 * приклеивалась к названию: «Смартфон realme note 60 +2400 бонусов». Это
 * не цена и не имя вещи — начисление, отдельной строкой, как рассрочка.
 */
const СТРОКА_БОНУСОВ = /^\s*[+±]\s*[\d\s  ]{1,9}\s*(бонус[\p{L}]*|балл[\p{L}]*|cashback|к[еэ]шб[еэ]к[\p{L}]*|сбер\s?спасибо|спасибо)\.?$/iu;

/**
 * Оценка в начале строки: «⭐ 4.8 ·», «4,8 ·», «★ 4.9 |».
 *
 * Озон делится с рейтингом впереди названия: «⭐ 4.8 · Наушники Sony». Звезда,
 * число до пяти и разделитель — это оценка, а не начало имени. Режем только
 * приставку в начале, а «4.8» внутри названия («iPhone 4.8 дюйма») не трогаем.
 * Форма «4,9 (1240 отзывов)» тоже режется; \p{L} вместо \w — иначе «отзывов»
 * не совпало бы, \w в JS кириллицу не ловит.
 */
const ОЦЕНКА_ВПЕРЕДИ = /^\s*[★☆⭐]?\s*[0-5][.,]\d\s*(?:\([\d\s\u00a0\u202f]+отзыв[\p{L}]*\)\s*|[·|•\-–—]\s*)/u;


/**
 * Ценник с оговоркой: «от 34 990 ₽», «12 000 ₽ −45%».
 *
 * Отдельной суммой такую строку не считаем (см. ценаИзПоделиться: цену «от»
 * осознанно не берём), но и в название пускать нельзя — иначе «Диван Милан
 * от 34 990 ₽» приезжает в поле «Что это». Заякорено жёстко: либо «от <сумма>»
 * целиком, либо «<сумма> −NN%» целиком. «Сертификат 5000 ₽» под это не подходит
 * и остаётся именем.
 */
const СТРОКА_ЦЕННИК = new RegExp(
  `^(?:от|from)\\s+[\\d\\s\\u00a0\\u202f]{1,15}(?:[.,]\\d{1,2})?\\s*(?:${ЗНАКИ_ВАЛЮТ})\\.?$` +
    `|^[\\d\\s\\u00a0\\u202f]{1,15}(?:[.,]\\d{1,2})?\\s*(?:${ЗНАКИ_ВАЛЮТ})\\s*[−–—-]\\s*\\d{1,2}\\s*%$`,
  'iu',
);

/**
 * Служебная подпись отдельной строкой: «Артикул: 20019283», «Продавец: JBL
 * Store», «SKU 12». Это метка магазина, не имя вещи. После слова обязателен
 * разделитель — двоеточие, № или пробел со значением, — чтобы «Артикульная
 * сумка» и «Продавцы года» остались названиями.
 */
const СТРОКА_СЛУЖЕБНАЯ = /^(?:артикул|код товара|sku|продавец|seller|поставщик)(?:\s*[:№]\s*|\s+)\S/iu;

/**
 * Призыв «смотрите на Маркете», «посмотрите в приложении» — приложение зовёт
 * открыть товар у себя, и это отдельной строкой уезжало в название. Имя вещи
 * с «Смотрите на» не начинается, а «Смотрите наши новинки» правило не задевает:
 * после «на»/«в» требуется пробел, которого в «наши» нет.
 */
const СТРОКА_ПРИЗЫВ = /^(?:смотрите|посмотрите)\s+(?:на|в)\s+\S/iu;

/**
 * Предисловие, которым приложение представляет то, чем делятся.
 *
 * Wildberries отдаёт «Смотри, что я нашла на Wildberries: Кроссовки женские»,
 * и без чистки всё это уезжает в поле «Что это». Режем только по двоеточию
 * и только когда до него стоит слово из приглашения — иначе пострадали бы
 * названия вроде «Набор: чашка и ложка».
 */
function убратьПредисловие(текст: string): string {
  /*
   * Границы слова тут выписаны руками: `\b` в JavaScript знает только
   * латиницу, и для неё «С» в «Смотри» — не буква вовсе. Правило с `\b`
   * не сработало бы ни разу.
   */
  /*
   * Восклицательный знак наравне с двоеточием.
   *
   * «Смотри что я нашёл! Кроссовки Nike Air Max» — так пишут чаще, чем
   * с двоеточием, и без этого весь призыв уезжал в поле «Что это».
   * Опасности тут нет: режем только когда строка НАЧИНАЕТСЯ со слова
   * из приглашения, а названия вроде «Ну и цены!» под это не подходят.
   */
  const предисловие =
    /^(?:[^:!]{0,60}[^\p{L}])?(?:смотри|смотрите|посмотри|посмотрите|глянь|наш[её]л|нашла|нашли)(?![\p{L}])[^:!]{0,40}[:!]\s*/iu;
  const короче = текст.replace(предисловие, '').trim();
  return короче.length >= 3 ? короче : текст;
}

/**
 * Приглашение установить приложение — хвост, который дописывает сам магазин.
 *
 * Идёт следом за тем, чем поделились: «Чайник Bosch https://shop.ru/p/1
 * Скачать приложение: https://apps.apple.com/…». Ссылку мы уже убрали,
 * а слова остались бы в названии желания.
 */
function убратьПриглашение(текст: string): string {
  /*
   * Глагол обязателен. Без него под правило попадало бы и название товара:
   * «Умные весы и приложение для здоровья» — это вещь, а не приглашение.
   */
  const приглашение =
    /(?:скача(?:ть|й|йте)|установ(?:и|ить|ите)|смотр(?:и|ите))\s+(?:наше\s+)?приложени[ияюе][^.]{0,30}$/iu;
  const короче = текст.replace(приглашение, '').replace(/[\s:;,—–|-]+$/u, '').trim();
  return короче.length >= 3 ? короче : текст;
}

/**
 * Хозяева ссылок, которые товаром не бывают никогда.
 *
 * Магазины приглашений (App Store, Google Play), мессенджеры и соцсети —
 * всё это приезжает вместе с настоящей ссылкой, а не вместо неё.
 */
const НЕ_ТОВАРНАЯ_ССЫЛКА =
  /^https?:\/\/(?:[^/]*\.)?(?:apps\.apple\.com|itunes\.apple\.com|play\.google\.com|appgallery\.(?:cloud\.)?huawei\.com|redirect\.appmetrica\.yandex\.com|onelink\.me|t\.me|telegram\.me|wa\.me|api\.whatsapp\.com|vk\.com\/(?:app|im)|instagram\.com|facebook\.com)/i;

/**
 * Разбор строки, вставленной в поле ссылки целиком.
 *
 * Кнопка «Поделиться» в приложении магазина отдаёт не голый адрес, а строку
 * вида «Куртка мужская Demix — Спортмастер https://sportmaster.ru/product/…».
 * Для площадок, закрытых от нашего сервера, это единственный путь, который
 * работает на любом телефоне: название приезжает от самого магазина, ставить
 * ничего не нужно. На iPhone он вдобавок единственный вообще — приёма ссылок
 * системой там нет.
 *
 * Возвращает `null`, когда разбирать нечего: строка и так один адрес или
 * ссылки в ней нет вовсе. Тогда поле ведёт себя как раньше, и человек, который
 * набирает адрес руками по букве, ничего не замечает.
 */
export function разобратьВставленнуюСтроку(
  значение: string,
): { адрес: string; название?: string; ценаМинор?: number; валюта?: string } | null {
  const адрес = pickSharedUrl({ url: значение, text: значение });
  if (!адрес || значение.trim() === адрес) return null;

  /*
   * Из названия убираем ВСЕ ссылки, а не только выбранную.
   *
   * Приложения магазинов дописывают приглашение себя установить: «Чайник Bosch
   * https://shop.ru/p/1 Скачать приложение: https://apps.apple.com/…». Убрав
   * только товарную, мы отдавали человеку желание с адресом App Store
   * в названии.
   */
  const безСсылок = значение.replace(/https?:\/\/[^\s<>"']+/gi, ' ');
  const название = названиеИзПоделиться(безСсылок);
  const цена = ценаИзПоделиться(безСсылок);

  return {
    адрес,
    ...(название ? { название } : {}),
    ...(цена ? { ценаМинор: цена.минор, ...(цена.валюта ? { валюта: цена.валюта } : {}) } : {}),
  };
}

/**
 * Цена из того, чем поделились, — если магазин вынес её отдельной строкой.
 *
 * Авито делится тремя строками: название, цена, ссылка. На закрытой площадке
 * это единственная цена, которую мы вообще можем узнать: до страницы наш
 * сервер не доходит, и человеку остаётся вписывать её руками. Раз магазин
 * сам её прислал — грех не взять.
 *
 * Берём ТОЛЬКО отдельную строку и только когда она в строке одна. Цена внутри
 * названия («Подарочный сертификат 5000 ₽») ценой товара не считается: там
 * это часть имени вещи, а не ценник. И «от 12 000 ₽» тоже не считается —
 * строка должна быть ровно суммой, без единого лишнего слова.
 */
function ценаИзПоделиться(raw: string): { минор: number; валюта?: string } | undefined {
  const ценники = raw
    .split(/[\r\n]+/)
    .map((строка) => строка.trim())
    .filter((строка) => ТОЛЬКО_ЦЕНА.test(строка));
  if (ценники.length !== 1) return undefined;

  const строка = ценники[0]!;
  const минор = ценаВКопейки(строка);
  if (минор === undefined) return undefined;

  const валюта = валютаПоЗнаку(строка);
  return валюта ? { минор, валюта } : { минор };
}

export function названиеИзПоделиться(raw: string | null | undefined): string | undefined {
  /*
   * Строку разбираем ПО СТРОКАМ, пока переводы не потерялись.
   *
   * Авито делится тремя строками: название, цена, ссылка. Склеенные в одну,
   * они дают желание «Велосипед Stels 12 000 ₽» — с ценой прямо в названии.
   * Строка, в которой нет ничего кроме суммы, частью названия не бывает,
   * а вот «Подарочный сертификат 5000 ₽» одной строкой — бывает, и его мы
   * не трогаем. Отсюда и разбор по строкам, а не по словам.
   */
  const строки = (raw ?? '')
    .split(/[\r\n]+/)
    .map((строка) => строка.trim())
    .filter((строка) => строка.length > 0 && !ТОЛЬКО_ЦЕНА.test(строка))
    .filter((строка) => !СТРОКА_РАССРОЧКИ.test(строка))
    .filter((строка) => !СТРОКА_БОНУСОВ.test(строка))
    .filter((строка) => !СТРОКА_ЦЕННИК.test(строка))
    .filter((строка) => !СТРОКА_СЛУЖЕБНАЯ.test(строка))
    .filter((строка) => !СТРОКА_ПРИЗЫВ.test(строка))
    .map((строка) => строка.replace(ОЦЕНКА_ВПЕРЕДИ, ''))
    .filter((строка) => строка.length > 0);

  const текст = убратьПриглашение(убратьПредисловие(строки.join(' ').replace(/\s+/g, ' ').trim()));
  if (текст.length < 3) return undefined;

  const заглушки = [
    /^ваши действия признаны автоматическими/i,
    /^подтвердите,? что вы не робот/i,
    /^привет,? робот/i,
    /^доступ (ограничен|запрещ[её]н|закрыт)/i,
    /^just a moment/i,
    /^attention required/i,
    /^\d{3}\s|^http[\s-]*\d{3}\b/i,
  ];
  if (заглушки.some((правило) => правило.test(текст))) return undefined;

  // Хвост магазина после длинного тире, вертикальной черты или двойного дефиса.
  const безХвоста = текст.replace(/\s*[—|–]\s*[^—|–]{0,40}$/u, '').trim();
  const итог = безХвоста.length >= 3 ? безХвоста : текст;
  /*
   * Дочищаем тем же кодом, что и название со страницы.
   *
   * Магазин, поделившийся товаром, зазывает ровно так же, как в заголовке:
   * «🔥 Скидка! Пылесос Dyson V15», «Купить кроссовки Nike». Разные правила
   * тут означали бы, что одно и то же желание выглядит по-разному в
   * зависимости от того, поделились им или прислали ссылку.
   */
  return очиститьНазваниеТовара(итог).slice(0, 200);
}

/**
 * Похожа ли страница на карточку товара, а не на витрину или статью.
 *
 * Одного признака мало: у витрины тоже есть разметка товара, у статьи бывает
 * og:image и цена в тексте. Поэтому считаем несколько независимых сигналов
 * и решаем по сумме — так ошибка одного не решает исход.
 *
 * Сигналы намеренно разной природы: разметка, служебные метки, вёрстка и
 * поведение. Если бы все они читали одно и то же место, набор из пяти был бы
 * не надёжнее одного.
 */
export interface ПризнакиТовара {
  /** Ровно один товар в разметке. У витрины их столько же, сколько плиток. */
  одинТоварВРазметке: boolean;
  /** Служебная метка og:type=product — её ставят именно на карточку. */
  меткаТовара: boolean;
  /** Что страница сама о себе объявляет в og:type: product, article, website. */
  типСтраницы: string;
  /** Есть цена и заголовок первого уровня: минимум настоящей карточки. */
  ценаИЗаголовок: boolean;
  /** Кнопка покупки. На витрине она у каждой плитки, здесь — про одну. */
  кнопкаПокупки: boolean;
  /** Хлебные крошки глубже двух уровней: признак конца пути, а не раздела. */
  глубокиеКрошки: boolean;
  /**
   * Похоже на витрину: много ссылок на другие товары и негде купить этот.
   *
   * Признак отрицательный и потому проверяется парой. Ссылок на товары полно
   * и у настоящей карточки — в блоке «похожие», — но там всегда есть кнопка
   * покупки. А у раздела её нет: покупать нечего, товар ещё не выбран.
   */
  похожеНаВитрину: boolean;
}

/**
 * Сколько страниц лежит ВНУТРИ этой — то есть глубже неё по адресу.
 *
 * Считать ссылки на товары по словам product и tovar в адресе оказалось мало:
 * у Колёс Даром товары живут в /catalog/, и с раздела «Центровочные кольца»
 * таких ссылок не видно ни одной, хотя на странице их семьдесят четыре.
 * Зато видно другое: два десятка ссылок ВГЛУБЬ самого раздела.
 *
 * Признак работает потому, что так устроены адреса: товар лежит внутри
 * раздела, а внутри товара не лежит ничего — разве что вкладки с отзывами
 * и характеристиками, и их единицы. Замер по корпусу: у настоящих карточек
 * от нуля до шести, у раздела двадцать восемь.
 */
function страницВглубь(doc: Document): number {
  const свой = адресСтраницы(doc);
  if (!свой) return 0;
  const путь = свой.pathname.endsWith('/') ? свой.pathname : `${свой.pathname}/`;

  const внутренние = new Set<string>();
  for (const узел of Array.from(doc.querySelectorAll('a[href]'))) {
    let адрес: URL;
    try {
      адрес = new URL(узел.getAttribute('href') ?? '', свой);
    } catch {
      continue;
    }
    if (адрес.host !== свой.host) continue;
    if (адрес.pathname !== свой.pathname && адрес.pathname.startsWith(путь)) внутренние.add(адрес.pathname);
  }
  return внутренние.size;
}

export function признакиТовара(doc: Document): ПризнакиТовара {
  let товаровВJsonLd = 0;
  for (const блок of Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))) {
    товаровВJsonLd += (блок.textContent?.match(/"@type"\s*:\s*"Product"/gi) ?? []).length;
  }
  const товаровВМикроразметке = doc.querySelectorAll('[itemscope][itemtype*="Product" i]').length;
  const всегоТоваров = товаровВJsonLd + товаровВМикроразметке;

  const meta = (имя: string): string =>
    doc.querySelector(`meta[property="${имя}"], meta[name="${имя}"]`)?.getAttribute('content') ?? '';

  const кнопки = Array.from(doc.querySelectorAll('button, a[role="button"], input[type="submit"]'))
    .map((узел) => `${узел.textContent ?? ''} ${узел.getAttribute('aria-label') ?? ''}`.toLowerCase())
    .filter(Boolean);

  const крошки =
    doc.querySelectorAll('[class*="breadcrumb" i] a, [itemtype*="BreadcrumbList" i] a, nav[aria-label*="хлеб" i] a')
      .length;

  const ссылокНаТовары = new Set(
    Array.from(doc.querySelectorAll('a[href]'))
      .map((узел) => узел.getAttribute('href') ?? '')
      .filter((href) => /\/(product|tovar|item|goods)[s]?\//i.test(href)),
  ).size;

  const кнопкаПокупки = кнопки.some((текст) =>
    /в корзину|добавить в корзину|купить|заказать|add to (cart|bag|basket)|buy now/i.test(текст),
  );

  return {
    одинТоварВРазметке: всегоТоваров === 1,
    меткаТовара: /product/i.test(meta('og:type')),
    типСтраницы: meta('og:type'),
    /*
     * Заголовок берём не только из h1.
     *
     * У re:Store на карточке h1 нет вовсе — название живёт в разметке и
     * в OpenGraph. Требовать именно h1 значит вычеркнуть такие магазины
     * из-за особенности их вёрстки, а не из-за сути страницы.
     */
    ценаИЗаголовок:
      Boolean(doc.querySelector('h1') ?? meta('og:title')) && главнаяЦенаНаСтранице(doc) !== undefined,
    кнопкаПокупки,
    глубокиеКрошки: крошки >= 3,
    /*
     * Два независимых способа увидеть витрину, порог у обоих один.
     *
     * Складывать их в одну сумму нельзя: у карточки Ситилинка шесть ссылок
     * по словам и шесть вкладок вглубь, и вместе они дотянули бы ровно до
     * порога — настоящая карточка уехала бы в разделы. Порознь каждый счёт
     * держится от него вдвое дальше.
     */
    похожеНаВитрину: (ссылокНаТовары >= 12 || страницВглубь(doc) >= 12) && !кнопкаПокупки,
  };
}

/**
 * Решение по сумме признаков.
 *
 * Много товаров в разметке — это витрина, и никакие другие признаки её не
 * оправдают: кнопка «в корзину» там есть у каждой плитки. В остальном хватает
 * двух независимых подтверждений: одного признака мало, трёх ждать незачем —
 * половина честных карточек не наберёт.
 */
/** Сколько товаров размечено на странице. У витрины — по одному на плитку. */
export function товаровВРазметке(doc: Document): number {
  return товарыВРазметке(doc).length + (нечитаемаяРазметкаТовара(doc) ? 1 : 0);
}

/**
 * Разметку товара видно в тексте, но разобрать её не удалось.
 *
 * Нужно, чтобы отличить «магазин ничего не разметил» от «магазин разметил,
 * а мы споткнулись»: у Bosco в JSON-LD живой перевод строки, и разбор на нём
 * падал. Наказывать карточку за опечатку магазина нельзя — в таком случае
 * судим по остальным приметам.
 */
function нечитаемаяРазметкаТовара(doc: Document): boolean {
  if (товарыВРазметке(doc).length > 0) return false;
  for (const блок of Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))) {
    if (/"@type"\s*:\s*"(Product|Book|Movie|VideoGame|MusicAlbum|Vehicle|Car|Apartment)"/i.test(блок.textContent ?? '')) {
      return true;
    }
  }
  return false;
}

/**
 * Описан ли на странице не товар, а целый ассортимент.
 *
 * Поймано на живых страницах. Раздел «Серебряные кольца» у Sunlight размечен
 * как ОДИН товар — со всеми признаками карточки, — но его предложение это
 * AggregateOffer: 3798 штук по цене от 312 до 19990 рублей. Правило «один
 * товар в разметке значит карточка» такие разделы пропускало, и в вишлист
 * уезжало желание «Серебряные кольца» за 312 рублей, то есть за самое дешёвое
 * кольцо в разделе.
 *
 * Отличие видно прямо в разметке и не зависит от магазина: у карточки цена
 * одна, у раздела — разброс или счётчик товаров больше одного. Товар
 * с размерами и цветами тоже размечают через AggregateOffer, но у него цены
 * совпадают, и он сюда не попадает.
 */
export function этоАссортимент(doc: Document): boolean {
  /*
   * Тот же разброс, записанный микроразметкой, а не JSON-LD.
   *
   * Поймано на живой странице Пульт.ру: раздел «Комплексные решения» размечен
   * как один товар, а предложение — AggregateOffer от 30 000 до 50 000 за три
   * штуки. Ровно тот случай, ради которого правило и написано, — но разбор
   * читал только JSON-LD, и раздел проходил как карточка. Одно и то же
   * утверждение магазина в двух записях должно читаться одинаково.
   */
  for (const узел of Array.from(doc.querySelectorAll('[itemscope][itemtype*="Product" i] [itemtype*="AggregateOffer" i]'))) {
    const поле = (имя: string): string | undefined => {
      const свойство = узел.querySelector(`[itemprop="${имя}"]`);
      return свойство?.getAttribute('content') ?? свойство?.textContent?.trim() ?? undefined;
    };
    const низ = числом(поле('lowPrice'));
    const верх = числом(поле('highPrice'));
    if (низ === 0 && верх === 0) return true;
    if (низ !== undefined && верх !== undefined && низ !== верх) {
      /*
       * Та же поблажка, что и в JSON-LD, и по тем же двум признакам: разброс
       * бывает не только у набора вещей, но и у одной вещи с размерами.
       *
       * Спортку́льт объявляет разброс дважды — и в JSON-LD, и здесь, — поэтому
       * починка одного места оставила бы карточку непризнанной. Хозяин
       * предложения тут находится подъёмом до ближайшей области товара:
       * у Спортку́льта это `<div itemscope itemtype="…/Product">` с
       * `<meta itemprop="sku" content="IO9566-400SV">` внутри и без счётчика
       * предложений. Правило и замер описаны в общем пакете, в
       * `разметкаНеОбещаетОднуВещь`.
       */
      const хозяин = узел.closest('[itemscope][itemtype*="Product" i]');
      const артикулХозяина = ПОЛЯ_АРТИКУЛА.some((имя) => {
        const свойство = хозяин?.querySelector(`[itemprop="${имя}"]`);
        const значение = свойство?.getAttribute('content') ?? свойство?.textContent?.trim() ?? '';
        return значение.trim().length > 0;
      });
      if (поле('offerCount') === undefined && артикулХозяина) continue;
      return true;
    }
  }

  for (const блок of Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))) {
    let данные: unknown;
    try {
      данные = разобратьРазметку(блок.textContent);
      if (данные === undefined) continue;
    } catch {
      continue;
    }
    for (const узел of развернутьУзлы(данные)) {
      const запись = узел as Record<string, unknown>;
      if (!этоТипВещи(запись['@type'])) continue;
      const предложения = Array.isArray(запись.offers) ? запись.offers : [запись.offers];
      for (const предложение of предложения) {
        const о = предложение as Record<string, unknown> | undefined;
        if (!о || !/aggregateoffer/i.test(String(о['@type'] ?? ''))) continue;
        /*
         * Судим по ценам, а не по счётчику товаров.
         *
         * Счётчик больше единицы бывает и у настоящей карточки: на площадках
         * один товар продают несколько продавцов, и offerCount считает их.
         * А вот разброс цен у одного товара означает ровно одно — это не один
         * товар. Разные размеры и цвета одной вещи стоят одинаково, и такая
         * карточка сюда не попадает.
         */
        const низ = числом(о.lowPrice);
        const верх = числом(о.highPrice);
        if (низ !== undefined && верх !== undefined && низ !== верх) {
          /*
           * Разброс — ещё не набор вещей: у одной вещи бывают размеры.
           * Правило и замер по обоим корпусам описаны в общем пакете,
           * в `разметкаНеОбещаетОднуВещь`; здесь его вторая половина, потому
           * что закладка живёт отдельным файлом и разметку читает сама.
           * Два признака вместе: магазин назвал артикул (одна модель) и
           * промолчал о числе предложений (считать нечего).
           */
          const артикулХозяина = ПОЛЯ_АРТИКУЛА.some((имя) => {
            const значение = запись[имя];
            if (typeof значение === 'number') return Number.isFinite(значение);
            return typeof значение === 'string' && значение.trim().length > 0;
          });
          if (числом(о.offerCount) === undefined && артикулХозяина) continue;
          return true;
        }
        /*
         * Разброс нулевой, потому что цен нет вовсе: «от 0 до 0, товаров 0».
         * Так размечен раздел «Все товары Liebherr» у Холодильник.ру. Товара,
         * который стоит ноль, не бывает, а вот пустой раздел — сплошь и рядом.
         */
        if (низ === 0 && верх === 0) return true;
      }
    }
  }
  return false;
}

/** Число из значения разметки: там цены пишут и числом, и строкой. */
function числом(значение: unknown): number | undefined {
  if (typeof значение === 'number') return Number.isFinite(значение) ? значение : undefined;
  if (typeof значение !== 'string') return undefined;
  const число = Number.parseFloat(значение.replace(',', '.'));
  return Number.isFinite(число) ? число : undefined;
}

/**
 * Разворачивает JSON-LD: он бывает объектом, массивом, графом и вложенным.
 *
 * Смотреть только на верхний уровень и на @graph оказалось мало. У Bosco товар
 * лежит внутри описания страницы, и обход его не находил: карточка духов
 * переставала опознаваться, хотя разметка на месте и полная.
 */
/**
 * Узлы разметки подряд — без рекурсии и с пределами.
 *
 * Рекурсивным он был, и проверка показала, чем это грозит: разметка глубиной
 * в пять тысяч уровней роняла `этоКарточкаТовара` с переполнением стека.
 * А такую глубину `JSON.parse` разбирает молча, любую. В браузере падение
 * страшнее, чем на сервере: кнопка закладки просто ничего не делает, и
 * человек не понимает, сломалось у него что-то или он не туда нажал.
 *
 * Та же беда была и в общем пакете, и чинить её надо было в обоих местах:
 * разборов два, и устойчивость у них тоже должна быть одна.
 *
 * Порядок сохранён прежний — сначала сам узел, потом вложенные, — чтобы
 * первым найденным товаром оставался тот же, что и раньше.
 */
const ПРЕДЕЛ_УЗЛОВ_РАЗМЕТКИ = 20_000;

function развернутьУзлы(данные: unknown): unknown[] {
  const собрано: unknown[] = [];
  const виденные = new Set<unknown>();
  const стопка: unknown[] = [данные];

  while (стопка.length > 0 && собрано.length < ПРЕДЕЛ_УЗЛОВ_РАЗМЕТКИ) {
    const узел = стопка.pop();
    if (!узел || typeof узел !== 'object') continue;
    if (виденные.has(узел)) continue;
    виденные.add(узел);

    if (Array.isArray(узел)) {
      for (let i = узел.length - 1; i >= 0; i -= 1) стопка.push(узел[i]);
      continue;
    }

    const запись = узел as Record<string, unknown>;
    собрано.push(запись);

    /*
     * `offers` пропускаем намеренно, как и раньше: предложение — это цена
     * и условия, а не отдельная вещь, и считать его товаром нельзя.
     */
    const ветки = Object.entries(запись)
      .filter(([ключ]) => ключ !== 'offers')
      .map(([, значение]) => значение)
      .filter((значение) => значение && typeof значение === 'object');
    for (let i = ветки.length - 1; i >= 0; i -= 1) стопка.push(ветки[i]);
  }

  return собрано;
}

/**
 * Сколько РАЗНЫХ цен написано на странице.
 *
 * Считаем именно разные: на карточке одна и та же цена повторяется в шапке,
 * в блоке покупки и в рассрочке, а на витрине каждая плитка приносит свою.
 * Замер по корпусу настоящих страниц: у карточек от 0 до 4, у разделов от 30.
 */
export function различныхЦенВТексте(doc: Document, хватит = 15): number {
  const текст = doc.body?.textContent ?? '';
  const правило = /(\d[\d\s\u00a0\u202f]{2,}(?:[.,]\d{2})?)\s*(?:₽|руб)/gi;
  const цены = new Set<string>();
  /*
   * Считаем не все цены, а до порога и останавливаемся.
   *
   * Этот подсчёт крутится в расширении на каждое изменение страницы, а на
   * витрине цен бывают сотни. Дочитывать их все незачем: вопрос стоит «больше
   * порога или нет», и ответ на него не меняется от трёхсотой цены.
   */
  for (let совпадение = правило.exec(текст); совпадение; совпадение = правило.exec(текст)) {
    цены.add((совпадение[1] ?? '').replace(/[\s\u00a0\u202f]/g, ''));
    if (цены.size >= хватит) break;
  }
  return цены.size;
}

/**
 * Описывает ли размеченный товар саму страницу — или это плитка на витрине.
 *
 * Правило «один товар в разметке значит карточка» держалось на допущении, что
 * магазин размечает предмет страницы. Корпус показал, что это не всегда так.
 * У Krasotka Pro на странице бренда размечена ровно одна плитка из каталога:
 * «Airnails, Трафареты №271» за 53 рубля, — а сама страница называется
 * «Каталог товаров бренда AIRnails». В вишлист уезжал каталог по цене
 * трафаретов.
 *
 * Проверяем не совпадение слово в слово, а долю слов товара, встречающихся
 * в названиях страницы. У карточки заголовок повторяет имя товара целиком или
 * почти; у витрины совпадает разве что бренд.
 */
function долиСовпадения(doc: Document, имяТовара: string): number {
  const слова = (текст: string): string[] =>
    текст
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .split(' ')
      .filter((слово) => слово.length >= 3);

  const словаТовара = слова(имяТовара);
  if (словаТовара.length === 0) return 1;

  const названияСтраницы = [
    doc.querySelector('h1')?.textContent ?? '',
    doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ?? '',
    doc.title,
  ].join(' ');
  const наСтранице = new Set(слова(названияСтраницы));
  if (наСтранице.size === 0) return 1;

  return словаТовара.filter((слово) => наСтранице.has(слово)).length / словаТовара.length;
}

export interface ТоварВРазметке {
  имя: string;
  ценаЕсть: boolean;
}

/**
 * Все товары, размеченные на странице, — и в JSON-LD, и в микроразметке.
 *
 * Раньше брался только первый, потому что вопрос стоял «а если он один».
 * Оказалось, что и на настоящей карточке их бывает несколько: Регард
 * размечает вместе с товаром его аналог, и страница карты памяти несёт
 * две разметки Product. Правило «больше одного — витрина» отвергало такую
 * карточку целиком, хотя название, цена и фотография доставались верно.
 */
export function товарыВРазметке(doc: Document): ТоварВРазметке[] {
  const найденные: ТоварВРазметке[] = [];

  const блоки: unknown[] = [];
  for (const блок of Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))) {
    try {
      const данные = разобратьРазметку(блок.textContent);
      if (данные !== undefined) блоки.push(данные);
    } catch {
      continue;
    }
  }

  /*
   * Ничейное предложение в ld+json — та же поправка, что и у микроразметки
   * Максидома, только на другом языке разметки. Спрашиваем один раз на всю
   * страницу: правило само по себе требует, чтобы предложение было
   * единственным, и складывать его по блокам нельзя.
   */
  const ничейнаяЦена = ничейнаяЦенаВРазметке(блоки);

  for (const данные of блоки) {
    const разновидности = разновидностиГруппы(данные);
    for (const узел of развернутьУзлы(данные)) {
      const запись = узел as Record<string, unknown>;
      if (!этоТипВещи(запись['@type'])) continue;
      // Разновидность группы — та же вещь в другом цвете, а не второй товар.
      if (разновидности.has(запись)) continue;
      const свои = Array.isArray(запись.offers) ? запись.offers : [запись.offers];
      const предложения = [...свои, ...предложенияРазновидностей(запись)];
      const ценаЕсть = предложения.some((п) => ценаПредложения(п) !== undefined);
      найденные.push({ имя: asText(запись.name) ?? '', ценаЕсть: ценаЕсть || ничейнаяЦена });
    }
  }

  const товарыМикроразметки = Array.from(doc.querySelectorAll('[itemscope][itemtype*="Product" i]'));
  for (const узел of товарыМикроразметки) {
    const свойство = (имя: string, сквозьПредложения = false): string | undefined => {
      for (const поле of Array.from(узел.querySelectorAll(`[itemprop="${имя}"]`))) {
        if (!полеПринадлежит(поле, узел, сквозьПредложения)) continue;
        const значение = поле.getAttribute('content')?.trim() || поле.textContent?.trim();
        if (значение) return значение;
      }
      return undefined;
    };
    const своя = Boolean(свойство('price', true) ?? свойство('lowPrice', true));
    найденные.push({
      имя: свойство('name') ?? '',
      ценаЕсть: своя || (товарыМикроразметки.length === 1 && естьНичейноеПредложение(doc)),
    });
  }

  return безПовторов(найденные);
}

/**
 * Своё ли это свойство товару — или его украла вложенная чужая область.
 *
 * Поймано на Здоровье.ру. Хлебные крошки там лежат ВНУТРИ области товара,
 * и первым `itemprop="name"` в документе оказывается «Главная» из крошки.
 * Прежний `узел.querySelector('[itemprop="name"]')` брал именно его — товар
 * получал имя «Главная», оно не сходилось ни с заголовком, ни с og:title,
 * страница переставала быть карточкой. Название и цену закладка при этом
 * доставала верно, из других источников: отвергалась разобранная страница.
 *
 * Правило микроданных ровно об этом: свойство принадлежит БЛИЖАЙШЕЙ
 * охватывающей области. Крошка объявляет свои `ListItem`, и её `name` —
 * свойство пункта списка, а не товара.
 *
 * Оговорка про предложения нужна для обычных карточек: там цена лежит
 * во вложенном `Offer`, и «только своё» отрезало бы её у всех сразу.
 * Поэтому для цены разрешаем спуск СКВОЗЬ области предложений — и только
 * сквозь них: чужой `Product` внутри (аналог, плитка «с этим покупают»)
 * своей ценой с хозяином не делится.
 */
function полеПринадлежит(поле: Element, узел: Element, сквозьПредложения: boolean): boolean {
  let область = поле.parentElement?.closest('[itemscope]') ?? null;
  while (область && область !== узел) {
    const типОбласти = область.getAttribute('itemtype') ?? '';
    if (!сквозьПредложения || !/Offer/i.test(типОбласти)) return false;
    область = область.parentElement?.closest('[itemscope]') ?? null;
  }
  return область === узел;
}

/**
 * Есть ли на странице предложение с ценой, у которого нет своего товара.
 *
 * Поймано на Максидоме. Область товара там кончается на заголовке
 * (`<div itemscope itemtype="…/Product">` вокруг одного `itemprop="name"`),
 * а цена лежит в отдельной области `…/Offer` — СОСЕДНЕЙ, не вложенной.
 * Разметка от этого не разваливается: у предложения хозяина нет, а товар
 * на странице один, и другого хозяина ему взять неоткуда.
 *
 * Без этого закладка объявляла страницу не товаром — и не потому, что
 * сомневалась, а по самой суровой ветке: «разметка есть, прочиталась
 * и не сошлась — довод ПРОТИВ». Цену она при этом видела и брала верно
 * (142 ₽, ту же, что и сервер), название тоже. То есть кнопки человек
 * не получал на странице, разобранной целиком и правильно.
 *
 * Серверный разбор так умел с самого начала: `extractMicrodata` ищет поля
 * в областях Product И Offer сразу (`packages/scraper/src/extract/meta.ts`).
 * Здесь мы догоняем его, а не заводим новое правило, — и разъехаться этим
 * двум ответам было нельзя.
 *
 * Ничейное — это ключ ко всей осторожности. У витрины каждая плитка несёт
 * своё предложение ВНУТРИ своего товара, и такое предложение сюда не
 * попадает: у него хозяин есть. Плюс спрашиваем только тогда, когда товар
 * в микроразметке ровно один: при нескольких неизвестно, чей это ценник,
 * а гадать — значит приписать цену не тому.
 */
function естьНичейноеПредложение(doc: Document): boolean {
  return ничейноеПредложение(doc) !== null;
}

/**
 * То самое ничейное предложение, а не только ответ «оно есть».
 *
 * Опознать страницу и достать с неё цену — два разных вопроса, и первое время
 * здесь отвечали только на первый. Стоило это ровно того, от чего заведён
 * корпус: на Максидоме закладка НАЧАЛА считать страницу карточкой и всё равно
 * оставляла поле цены пустым, хотя серверный разбор из тех же байтов брал
 * 142 ₽. Человек получал кнопку, название и фото — и пустую цену, молча.
 *
 * Поэтому предложение возвращается целиком: и опознание, и сбор цены спрашивают
 * ОДНО место одним правилом. Разъедься они снова — и страница опять окажется
 * карточкой без цены, только уже по другой причине.
 */
function ничейноеПредложение(doc: Document): Element | null {
  for (const предложение of Array.from(doc.querySelectorAll('[itemscope][itemtype*="Offer" i]'))) {
    if (предложение.parentElement?.closest('[itemscope]')) continue;
    if (полеПредложения(предложение, 'price') ?? полеПредложения(предложение, 'lowPrice')) return предложение;
  }
  return null;
}

/** Поле предложения так, как его записал магазин: `content` или текст. */
function полеПредложения(предложение: Element, имя: string): string | undefined {
  const поле = предложение.querySelector(`[itemprop="${имя}"]`);
  const значение = поле?.getAttribute('content')?.trim() || поле?.textContent?.trim();
  return значение || undefined;
}

/**
 * Одна и та же вещь, размеченная несколько раз, — это одна вещь.
 *
 * Магазины ссылаются на товар из разных мест разметки: у Читай-города книга
 * «Невеста для снежного волка» размечена девять раз — в предложении, в действии
 * «читать», в описании страницы. Пока они считались по отдельности, страница
 * выглядела витриной из девяти товаров, и кнопка на ней не появлялась.
 *
 * Считаем по имени: цену подхватываем от любого из повторов, потому что она
 * стоит обычно только в одном.
 */
function безПовторов(товары: ТоварВРазметке[]): ТоварВРазметке[] {
  const поИмени = new Map<string, ТоварВРазметке>();
  for (const товар of товары) {
    const ключ = товар.имя.toLowerCase().replace(/\s+/g, ' ').trim();
    const прежний = поИмени.get(ключ);
    if (!прежний || (товар.ценаЕсть && !прежний.ценаЕсть)) поИмени.set(ключ, товар);
  }
  return [...поИмени.values()];
}

/** Имя и цена первого размеченного товара. Оставлено для замеров и проверок. */
export function единственныйТовар(doc: Document): ТоварВРазметке | undefined {
  return товарыВРазметке(doc)[0];
}

/**
 * Который из размеченных товаров и есть предмет страницы.
 *
 * У карточки с блоком «похожие» размечено несколько товаров, и все они об одном
 * и том же: у Регарда рядом с картой памяти Transcend 133x размечена Transcend
 * 1000x, и по доле общих слов с заголовком она проходит порог не хуже. Поэтому
 * судим не по порогу, а по превосходству: предмет страницы — тот, чьё имя
 * совпадает с заголовком ЛУЧШЕ всех остальных.
 *
 * Если лучших несколько — обычно выделить предмет страницы нельзя, и это
 * витрина: там плитки равноправны. Исключение одно и разобрано ниже по коду —
 * равными бывают два имени ОДНОЙ вещи, размеченной дважды.
 */
function предметСтраницы(doc: Document, товары: ТоварВРазметке[]): ТоварВРазметке | undefined {
  const сОценкой = товары
    .map((товар) => ({ товар, доля: долиСовпадения(doc, товар.имя) }))
    .filter((запись) => запись.доля >= 0.4);
  if (сОценкой.length === 0) return undefined;

  const лучшая = Math.max(...сОценкой.map((запись) => запись.доля));
  const лидеры = сОценкой.filter((запись) => запись.доля === лучшая);
  if (лидеры.length === 1) return лидеры[0]?.товар;

  /*
   * Несколько лучших — ещё не витрина, если все они об ОДНОЙ вещи.
   *
   * Магазин описывает товар дважды, на двух языках разметки, и имена при этом
   * не обязаны совпадать буква в букву. Котофото: в JSON-LD «Ноутбук MSI
   * Vector 17 HX… (9S7-17S372-063)», в микроразметке — то же самое плюс хвост
   * «в Санкт-Петербурге», взятый из h1. Повторы отсеиваются по имени, а эти
   * два имени разные, и `безПовторов` их не склеивает.
   *
   * Прежде разницу создавала сама поломка: в JSON-LD стояло `17&quot;`,
   * нерасшифрованная мнемоника давала лишнее слово «quot», доля падала — и
   * ничьей не было. Починив расшифровку, я вскрыла то, что она прикрывала:
   * страница с одним товаром объявлялась витриной, и кнопки на ней человек
   * не видел.
   *
   * Одна вещь или разные — видно по словам: у двух имён одной вещи слова
   * одного вложены в слова другого целиком. У плиток витрины так не бывает,
   * они различаются именно словами. Предметом берём самое полное имя: хвост
   * города лишний, но он не врёт, а короткое имя — часть длинного.
   */
  const словаИмени = (имя: string) =>
    new Set(
      имя
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .split(' ')
        .filter((слово) => слово.length >= 3),
    );
  const наборы = лидеры.map((запись) => ({ запись, слова: словаИмени(запись.товар.имя) }));
  const самыйПолный = наборы.reduce((а, б) => (б.слова.size > а.слова.size ? б : а));
  const всеВложены = наборы.every((н) => [...н.слова].every((слово) => самыйПолный.слова.has(слово)));

  if (!всеВложены) return undefined;

  /*
   * Цену берём от любого из повторов — так же, как в `безПовторов`. Магазин
   * ставит её обычно в одном языке разметки: у Котофото цена есть у обоих,
   * но требовать этого нельзя, иначе вещь с ценой только в JSON-LD останется
   * без предмета страницы из-за второго, безценового имени.
   */
  return {
    имя: самыйПолный.запись.товар.имя,
    ценаЕсть: лидеры.some((запись) => запись.товар.ценаЕсть),
  };
}

/*
 * Примета чтива переехала в общий пакет: по ней судят оба разбора, и
 * разъехаться им нельзя. Пояснение и случаи из корпуса — там же.
 */

/**
 * Есть ли в разметке хоть что-то, чем товар отличают от других: артикул,
 * штрихкод, код производителя, бренд.
 *
 * Раздел, выданный за товар, такими полями не снабжают — их просто неоткуда
 * взять. Сам по себе признак слабый: у пяти настоящих карточек корпуса ничего
 * этого тоже нет, поэтому в одиночку он ничего не решает.
 */
function опознавательТовара(doc: Document): boolean {
  const разметка = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'))
    .map((блок) => блок.textContent ?? '')
    .join(' ');
  if (/"(sku|gtin\d*|mpn|productID)"\s*:/i.test(разметка)) return true;
  const узел = doc.querySelector('[itemscope][itemtype*="Product" i]');
  return Boolean(узел?.querySelector('[itemprop="sku"], [itemprop^="gtin"], [itemprop="mpn"], [itemprop="brand"], [itemprop="productID"]'));
}

/**
 * Есть ли в адресе опознавательный кусок: длинное имя или номер.
 *
 * Смотрим по ВСЕМУ пути, а не по последнему куску. Разница не косметическая:
 * у Wildberries карточка живёт по адресу /catalog/12345678/detail.aspx, и по
 * последнему куску — коротенькому «detail.aspx» — она считалась бы разделом.
 * Ни одна страница корпуса от такой замены не поменяла вердикт, зато три
 * крупнейшие площадки перестали попадать под нож.
 */
/*
 * Правило общее с серверным разбором и живёт в общем пакете: по нему решается,
 * карточка перед человеком или раздел, и разъехаться этим двум ответам нельзя.
 */
/**
 * Описание, набранное прямо на странице, — когда в разметке его нет.
 *
 * У Глобуса единственное описание в разметке — визитка сети «Гипермаркеты
 * «Глобус». Официальный сайт.», и мы её справедливо выбрасываем. А на самой
 * странице под заголовком «О товаре» написан состав мороженого — то, ради чего
 * человек описание и читает. Закладка эту страницу видит нарисованной целиком,
 * так что взять текст ей ничего не мешает.
 *
 * Берём осторожно и только по заголовку: ищем подпись «Описание», «О товаре»,
 * «Состав» — и текст того куска, в котором она стоит. Ни поиска «самого
 * длинного абзаца», ни угадывания по классам: и то, и другое на живой странице
 * приносит отзывы, доставку и подвал сайта.
 */
const ЗАГОЛОВОК_ОПИСАНИЯ = /^\s*(описание|о товаре|подробнее о товаре|описание товара)\s*:?\s*$/i;

/** Ниже этой длины кусок текста — подпись, ярлычок или артикул, а не рассказ о вещи. */
const ДЛИНА_РАССКАЗА = 40;

/**
 * Спуск от куска с заголовком к куску с самим текстом.
 *
 * Заголовок стоит рядом с описанием, но родитель у них общий и часто держит
 * ещё и обвязку. У befree это вешалка-гармошка: под подписью «описание товара»
 * лежат цвет, точка, число 99 и артикул 2136302113 — и только потом сам
 * рассказ. Человек читал «мультиколор • 99 2136302113 - Комплект из двух
 * трикотажных трусиков…» и первые три слова понимал как мусор.
 *
 * Спускаемся, пока у куска ровно ОДИН весомый ребёнок: тогда весь текст лежит
 * в нём, а остальные дети — подписи и значки. Двое весомых — останавливаемся:
 * выбор одного из них был бы уже угадыванием.
 *
 * То же самое делает серверный разбор в `packages/scraper/src/extract/meta.ts`,
 * и делать это обязаны оба: страницу человек может прислать ссылкой, а может
 * открыть у себя.
 */
function сузитьДоТекста(кусок: Element): Element {
  let текущий = кусок;

  // Предел спуска — от разрастания вёрстки, а не от смысла.
  for (let шаг = 0; шаг < 10; шаг += 1) {
    const весомые = Array.from(текущий.children).filter(
      (ребёнок) => (ребёнок.textContent ?? '').trim().length >= ДЛИНА_РАССКАЗА,
    );
    if (весомые.length !== 1) break;
    текущий = весомые[0]!;
  }

  return текущий;
}

function описаниеИзВёрстки(doc: Document): string | undefined {
  /*
   * «Состав» в этот список не входит намеренно. У MIXIT под ним лежит перечень
   * из полусотни латинских названий — Glycerin, Aqua, Xylitol и так далее.
   * Формально это описание, а по сути в вишлисте оно бесполезно: человек хочет
   * прочитать, что за вещь, а не её химическую формулу.
   */
  for (const узел of Array.from(doc.querySelectorAll('h2, h3, h4, div, span, button'))) {
    if (!ЗАГОЛОВОК_ОПИСАНИЯ.test(узел.textContent ?? '')) continue;

    const кусок = узел.parentElement;
    if (!кусок) continue;

    const весь = описаниеБезРазметки(сузитьДоТекста(кусок).textContent ?? '');
    /*
     * Отрезаем сам заголовок: он не описание, а его подпись. Подпись в начале
     * строки снимает уже `описаниеБезРазметки` — здесь остаётся случай, когда
     * от куска НИЧЕГО, кроме подписи, не пришло.
     *
     * Раньше тут стояло ещё и слово «состав». Оно было заплаткой на склейку:
     * `textContent` шёл встык, у Глобуса получалось «О товареСостав клубника
     * свежемороженая…», и первый проход снимал только «о товаре». Теперь
     * склейку разбирает общая чистка, и это слово съедало бы у человека
     * настоящую подпись состава, а не служебную.
     */
    const текст = весь.replace(ЗАГОЛОВОК_ОПИСАНИЯ, '').trim();
    /*
     * Слишком короткое — это подпись без текста, слишком длинное — значит
     * заголовок стоял в шапке большого куска и мы забрали полстраницы.
     */
    if (текст.length < 40 || текст.length > 2_000) continue;
    return текст.slice(0, 500);
  }
  return undefined;
}

function адресПохожНаТовар(doc: Document): boolean {
  return адресПохожНаТоваруОбщее(адресСтраницы(doc));
}

/**
 * Слова, которыми магазин просит покупателя назвать сумму сам.
 *
 * Именно просьба, а не любое упоминание суммы: «введите свою сумму»,
 * «выберите номинал». Так подписывают поле только там, где цены у товара нет.
 */
/* Словарь переехал в общий пакет: по нему судят оба разбора. */

/**
 * Цену здесь назначает покупатель, а не магазин.
 *
 * Поймано на подарочном сертификате BESTWATCH: «Выберите номинал сертификата»,
 * восемь кнопок с суммами и поле «или введите свою сумму» от 1 000 до 150 000.
 * В разметке при этом стоит цена 10 000 — значение по умолчанию, — и страница
 * проходила как обычная карточка. В вишлист уезжало желание «Certificate
 * certificate_custom» за десять тысяч: ни названия, ни настоящей цены.
 *
 * Сертификат с готовым номиналом сюда не попадает, и правильно: у него цена
 * одна, магазин её назвал, и это обычный товар. Отличает конструктор ровно
 * поле для суммы — цены у страницы нет, пока её не вписал человек.
 *
 * Поле для количества похоже, но отличимо: у него либо нет предела, либо предел
 * штучный. Поэтому спрашиваем и про подпись, и про денежный размах.
 */
function ценуНазначаетПокупатель(doc: Document): boolean {
  for (const поле of Array.from(doc.querySelectorAll('input'))) {
    const тип = (поле.getAttribute('type') ?? '').toLowerCase();
    const числовое = тип === 'number' || (поле.getAttribute('inputmode') ?? '').toLowerCase() === 'numeric';
    if (!числовое) continue;

    const верх = Number.parseInt(поле.getAttribute('max') ?? '', 10);
    const низ = Number.parseInt(поле.getAttribute('min') ?? '', 10);
    const денежныйРазмах = (Number.isFinite(верх) && верх >= 1000) || (Number.isFinite(низ) && низ >= 500);
    if (!денежныйРазмах) continue;

    // Подпись бывает и рядом с полем, и на всём блоке формы — смотрим вверх
    // на несколько уровней, но не до самой страницы: там слово «номинал»
    // найдётся в любом описании сертификата, включая готовый.
    let окрестность = '';
    let узел: Element | null = поле.parentElement;
    for (let шаг = 0; шаг < 3 && узел; шаг += 1) {
      окрестность = узел.textContent ?? '';
      узел = узел.parentElement;
    }
    if (ПРОСЬБА_НАЗВАТЬ_СУММУ.test(окрестность)) return true;
  }
  return false;
}

/**
 * Адрес страницы, если это действительно страница в сети.
 *
 * Отдельно, потому что doc.URL бывает и служебным: about:blank у документа,
 * собранного в памяти. Такой адрес разбирается без ошибки и даёт «blank»
 * последним куском пути — по нему правила об адресе выносили вердикт,
 * которого не имели права выносить.
 */
function адресСтраницы(doc: Document): URL | undefined {
  try {
    const адрес = new URL(doc.URL);
    return адрес.protocol === 'http:' || адрес.protocol === 'https:' ? адрес : undefined;
  } catch {
    return undefined;
  }
}

export function этоКарточкаТовара(doc: Document): boolean {
  /*
   * Адрес страницы оказался честнее её разметки.
   *
   * Корпус показал расхождение в обе стороны сразу. У Подписных изданий статья
   * «Отрывок из книги» объявляет себя товаром через og:type=product, и в вишлист
   * уезжал отрывок по цене книги из врезки. А у Bosco настоящая карточка духов
   * объявляет себя статьёй. Верить метке нельзя ни в ту, ни в другую сторону,
   * а вот раздел адреса магазин выбирает осознанно и меняет редко.
   */
  const адрес = адресСтраницы(doc);
  if (адрес && адресГоворитЭтоЧтиво(адрес)) return false;

  /*
   * Страница сама назвалась служебной — спорить не с чем.
   *
   * Из корпуса: раздел зеркальных фотоаппаратов у Фотосклада называется
   * «Главная страница», а по битой ссылке Baon отдаёт «Baon: Интернет-магазин».
   * Обе страницы проходили все проверки ниже — с ценой, с фотографией, со
   * всем, — и человек получал желание «Главная страница» по цене случайного
   * фотоаппарата из сетки.
   */
  /*
   * Спрашиваем ВСЕ три источника названия, а не первый попавшийся.
   *
   * Сначала здесь стоял «h1, иначе og:title, иначе title», и на нарисованной
   * странице Фотосклада это дало осечку: в h1 честное «Зеркальные фотоаппараты
   * kit», а в заголовке окна — «Главная страница». Заголовок окна пишет тот же
   * магазин и про ту же страницу; если он называет её главной, спорить не с чем,
   * даже когда посреди страницы нарисован заголовок раздела. У X-Com то же
   * самое со словом «Главная».
   */
  /*
   * Служебным должно быть КАЖДОЕ имя, а не какое-нибудь одно. Правку принесла
   * живая страница Wildberries 07.08.2026.
   *
   * Прежде здесь стояло «хоть один источник служебный — не карточка», и
   * рассуждение было такое: заголовки пишет один и тот же магазин про одну
   * и ту же страницу, значит они не спорят. Маркетплейс это опроверг.
   * У страницы товара ВБ нет h1 вовсе, в og:title лежит вывеска всего сайта
   * («Интернет‑магазин Wildberries: широкий ассортимент товаров»), а
   * в заголовке окна — честное «Набор 4 шт Лента Металлизированная… купить
   * за 1 630 ₽». Одна разметка на весь сайт — обычное дело для площадки,
   * и по прежнему правилу КАЖДАЯ карточка Wildberries объявлялась не товаром:
   * расширение не показывало кнопку, закладка отказывала.
   *
   * Вылезло это только сейчас не случайно. Вывеска ВБ написана через
   * неразрывный дефис U+2011, а `названиеСлужебнойСтраницы` искала обычный —
   * то есть правило молчало по недосмотру. Починили дефисы, и стал виден
   * настоящий счёт.
   *
   * Замер по нарисованному корпусу, 71 страница: хоть одно служебное имя
   * есть у ДВУХ. У Baon это единственное имя вообще («Baon.ru —
   * интернет-магазин одежды…»), и он остаётся отвергнут обоими правилами.
   * Расходятся правила ровно на Wildberries. То есть строгость, которую
   * снимаем, за всё время не отвергла ни одной страницы сверх — а одну
   * настоящую карточку отвергала.
   *
   * Страницы, ради которых источников три, эту проверку и так не проходят
   * по другим приметам: у Фотосклада и X-Com служебного имени сейчас нет
   * ни в одном источнике, разделами их называют счёт товаров и адрес.
   */
  const названия = [
    doc.querySelector('h1')?.textContent,
    doc.querySelector('meta[property="og:title"]')?.getAttribute('content'),
    doc.title,
  ].filter((имя): имя is string => Boolean(имя && имя.trim()));
  if (
    названия.length > 0 &&
    названия.every((имя) => названиеСлужебнойСтраницы(очиститьНазваниеТовара(имя)))
  ) {
    return false;
  }

  /*
   * Страница-конструктор: цены у неё нет, пока покупатель не назвал её сам.
   * Проверяем до разметки — у сертификата с произвольной суммой разметка
   * полная и честная на вид, в ней просто стоит значение по умолчанию.
   */
  if (ценуНазначаетПокупатель(doc)) return false;

  const товаров = товаровВРазметке(doc);
  const товары = товарыВРазметке(doc);
  const предмет = предметСтраницы(doc, товары);

  /*
   * Много товаров — витрина, если среди них не выделяется предмет страницы.
   *
   * Раньше здесь стоял безусловный отказ, и он стоил настоящих карточек:
   * Регард размечает вместе с товаром его аналог, и страница карты памяти
   * несёт две разметки Product. Название, цена и фотография доставались
   * верно, а кнопки на такой странице человек не видел.
   *
   * Витрину от карточки с «похожими» отличает не число товаров, а то, есть ли
   * среди них главный: на витрине плитки равноправны, и заголовок страницы
   * не повторяет ни одну из них лучше прочих.
   */
  if (товаров > 1 && предмет === undefined) return false;

  /*
   * Несколько товаров в разметке и адрес без опознавателя — это витрина.
   *
   * Последний из шести известных пробелов, и самый упрямый: раздел «Верхняя
   * женская одежда» у Love Republic размечен восемью товарами, с артикулом
   * и брендом — по разметке он честнее иной настоящей карточки, и все правила
   * ниже он проходил. Отличает его адрес: /catalog/odezhda/verhnyaya-odezhda/,
   * без единого номера. У Регарда, где товаров в разметке тоже восемь (карточка
   * и её аналог), адрес /product/73872/… — магазин обязан был выдать товару
   * опознаватель, иначе на него нельзя сослаться.
   *
   * Честно про основание: в обоих корпусах страниц с несколькими размеченными
   * товарами ровно две — эта и регардовская, — и правило разделяет их начисто.
   * Двух примеров мало, чтобы считать правило доказанным, поэтому оно стоит
   * последним и срабатывает только вместе с «товаров больше одного»: обычной
   * карточки с единственной разметкой оно не касается вовсе.
   */
  if (товаров > 1 && !адресПохожНаТовар(doc)) return false;

  const признакиСтраницы = признакиТовара(doc);

  /*
   * Раздел, где размечен ровно один товар, — так бывает чаще, чем хотелось бы.
   *
   * Поймано на живой странице «Все товары Liebherr»: в разметке один товар,
   * заголовок и цена на месте, и правило «один товар — значит карточка»
   * записывало раздел в карточки. Отличает их то, что покупать на разделе
   * нечего: кнопки нет, а ссылок на другие товары полно.
   */
  if (признакиСтраницы.похожеНаВитрину) return false;

  // Раздел, размеченный как один товар с разбросом цен. Проверяем до вердикта
  // «один товар — значит карточка», иначе он такие страницы пропускает.
  if (этоАссортимент(doc)) return false;

  /*
   * Ровно один размеченный товар — этого достаточно самого по себе.
   *
   * Требовать сюда второй признак было ошибкой: честная карточка с разметкой,
   * но без видимой цены в тексте и без кнопки в разметке, переставала
   * опознаваться. Магазин прямо объявил «здесь один товар» — спорить не с чем.
   */
  if (товаров >= 1) {
    /*
     * Верим объявлению магазина, но только когда оно про эту страницу
     * и подтверждено ценой. Проверяем и то, и другое: размеченный товар
     * должен быть предметом страницы, а не плиткой на витрине.
     */
    if (предмет?.ценаЕсть) {
      /*
       * Разметка убедительна — но не когда в ней нет вообще ничего о товаре,
       * а адрес выглядит разделом.
       *
       * У ZENDEN раздел «Зонты» размечен как товар: имя совпадает с заголовком,
       * цена берётся с плитки. Отличает его то, что в разметке нет ни артикула,
       * ни бренда, ни штрихкода, а в адресе стоит umbrellas. Каждый признак
       * по отдельности бесполезен: без артикула живут и пять настоящих карточек
       * корпуса, а короткий адрес встречается у товаров с числовым номером.
       * Вместе они не совпали ни на одной настоящей карточке.
       */
      if (!опознавательТовара(doc) && !адресПохожНаТовар(doc)) return false;
      return true;
    }
    /*
     * Разметку видно, но прочитать не удалось — судим по остальным приметам.
     *
     * У Bosco в JSON-LD ошибка, и разбор на нём спотыкается, хотя товар там
     * размечен полностью. Считать нечитаемую разметку доводом против значило бы
     * наказывать карточку за опечатку магазина: духи переставали опознаваться.
     */
    if (товары.length === 0) return общиеПриметы(doc, признакиСтраницы);
    /*
     * Разметка НЕ ПРО ЭТУ СТРАНИЦУ — вот это довод ПРОТИВ.
     *
     * Сначала я дал таким страницам провалиться в общую ветку, и они спокойно
     * проходили её по цене с плитки: страница бренда AIRnails у Krasotka Pro
     * размечена одним товаром — «Airnails, Трафареты №271», первой плиткой
     * каталога, — а заголовок у неё «AIRnails». Магазин разметил не то, что
     * показывает; значит верить остальным приметам тем более незачем.
     */
    if (предмет === undefined) return false;
    /*
     * А вот объявление ПРО ЭТУ СТРАНИЦУ, но без цены, доводом против не
     * является. Раньше здесь стоял общий отказ на оба случая, и вот чем он
     * был обоснован: «без цены объявление ничего не подтверждает — у Pult
     * и ZENDEN так размечены разделы „Комплексные решения“ и „Зонты“».
     *
     * Замер по корпусу из 103 страниц показал, что это неправда, и оба
     * примера названы мимо. У ZENDEN в разметке цена ЕСТЬ (39 900, взятая
     * с плитки) — его отвергает правило выше, про артикул и адрес. Pult
     * тоже размечен с ценой и отсеивается ещё раньше, как ассортимент
     * с разбросом цен. До этого места ни тот, ни другой не доходят вовсе.
     *
     * Кто доходит — весь список, целиком: Пятый элемент («Холодильник Haier
     * C4F640CWU1»), АСНА («Диет Формула Фитодиурекс Калий+»), Игровед
     * («Настольная игра Коварный крот»). Все три — НАСТОЯЩИЕ КАРТОЧКИ, и имя
     * в разметке у всех трёх совпадает с заголовком слово в слово. Разделов
     * с размеченным без цены предметом страницы в корпусе нет ни одного.
     *
     * То есть правило ловило ровно то, чего боялось не поймать, — и платило
     * за это тремя карточками. Отсутствие цены в разметке значит только одно:
     * магазин держит цену не там. Судим по общим приметам, как и в случае
     * с нечитаемой разметкой, — а это не поблажка: они строгие и сами
     * отвергают две из трёх (у АСНЫ полтора десятка разных цен в тексте,
     * у Игроведа адрес раздела и метка статьи).
     */
    /*
     * «Держит цену не там» — так найдём её там, где он её держит.
     *
     * Это и есть закрытие пробела Игроведа. Общие приметы отвергали его
     * ТРИЖДЫ, и все три раза — по мелочи вёрстки: адрес /games/sly-mole/
     * не похож на товар, `og:type=article`, а видимой цены рядом с заголовком
     * нет вовсе. Подгонять под этот случай сами приметы нельзя: они общие,
     * и каждое послабление в них оплачивается чужими разделами.
     *
     * Но спрашивать их тут и не за что. У страницы есть два независимых
     * заявления самогó магазина об одном и том же предмете: разметка говорит
     * «предмет этой страницы — Настольная игра Коварный крот» (слово в слово
     * с заголовком, иначе сюда не дошли бы — выше стоит `предмет === undefined`),
     * а счётчик рядом говорит «Коварный крот стоит 990». Гадать по приметам
     * после двух прямых заявлений незачем: приметы — для страниц, о которых
     * магазин не сказал ничего.
     *
     * Ровно ту же цену и ровно из того же места достаёт извлекатель — и
     * серверный (`extract/state.ts`), и браузерный. То есть опознаватель
     * не учится новому источнику, а перестаёт не знать того, что разбор
     * рядом с ним уже знает.
     *
     * Замер по корпусу (103 страницы): цена того же предмета в счётчиках
     * есть у 47 страниц, но до этой ветки из них доходят двое — Игровед
     * и Пятый элемент (последний и так опознавался). Оба раздела корпуса
     * со счётчиковой ценой сюда не попадают вовсе: у bestwatch цена в разметке
     * есть, у Tiu разметки нет ни одной. Витрины и ассортимент отсеяны ещё
     * выше, до этой ветки.
     */
    if (ценаПредметаВСчётчиках(doc, предмет.имя)) return true;
    return общиеПриметы(doc, признакиСтраницы);
  }

  /*
   * Разметки нет вовсе — тогда нужны два независимых подтверждения.
   * Одного мало: кнопка «купить» встречается и на витрине, и в шапке сайта,
   * а цена в тексте бывает и в статье «как выбрать кроссовки».
   */
  /*
   * Разметки нет — тогда смотрим на разброс цен.
   *
   * Поймано на разделе X-Com: разметки никакой, зато двадцать четыре кнопки
   * «купить» и тридцать разных цен. Слабые признаки набирали свои два голоса,
   * и раздел уезжал в карточки под названием «Главная». На настоящей карточке
   * без разметки столько разных цен взяться неоткуда.
   *
   * Порог держим с большим запасом от обеих границ замера, потому что на живой
   * странице к карточке снизу приклеены «похожие товары», и несколько чужих цен
   * там будут всегда.
   */
  return общиеПриметы(doc, признакиСтраницы);
}

/**
 * Цена ПРЕДМЕТА ЭТОЙ СТРАНИЦЫ, взятая из скриптов счётчиков.
 *
 * Нужна там, где магазин разметил товар без цены, а саму цену держит в
 * аналитике: у Игроведа страница «Настольная игра Коварный крот» отдаёт
 * её только так — `dataLayer.product_page({ "name": "Коварный крот",
 * "price": 990 })`.
 *
 * Три осторожности, и каждая уже оплачена чужой ошибкой — теми же тремя,
 * что стоят у серверного близнеца в `extract/state.ts`:
 *
 * 1. Блоки состояния не читаем. В плоской записи Nuxt значение поля — это
 *    номер ячейки, и чтение текстом выдало бы номер за сумму: у Mixit гель
 *    за 685 ₽ так получал 1492 ₽ — стоимость доставки из ячейки 1492.
 * 2. Имя обязано быть в ТОМ ЖЕ скрипте и обязано называть тот же предмет.
 *    В счётчиках рядом лежат «недавно смотрели» и «с этим покупают», и цена
 *    любого из них выглядит настоящей. Считаем долю слов ИМЕНИ ИЗ СКРИПТА,
 *    найденных в имени предмета, а не наоборот: в разметке имя длиннее
 *    («Настольная игра Коварный крот» против «Коварный крот»), и обратный
 *    счёт дал бы 0.5 при полном совпадении названия вещи.
 * 3. Ноль — не цена, а заглушка счётчика: у Baon в скрипте стоит
 *    `"price": 0`, и без этого страница получала бы уверенное и неверное «0».
 *
 * Возвращаем не сумму, а «есть или нет»: опознавателю нужен сам факт, что
 * магазин назвал цену этой вещи. Цену для карточки достаёт извлекатель,
 * и делать это дважды разными путями — верный способ развести два ответа.
 */
function ценаПредметаВСчётчиках(doc: Document, имяПредмета: string): boolean {
  const словаПредмета = new Set(ключевыеСлова(имяПредмета));
  if (словаПредмета.size === 0) return false;

  for (const текст of скриптыСЦеной(doc)) {
    const цена = ЦЕНА_В_СКРИПТЕ.exec(текст);
    const имя = ИМЯ_В_СКРИПТЕ.exec(текст);
    if (!цена?.[1] || !имя?.[1]) continue;

    const словаИмени = ключевыеСлова(раскодировать(имя[1]));
    if (словаИмени.length === 0) continue;
    const совпало = словаИмени.filter((слово) => словаПредмета.has(слово)).length;
    if (совпало / словаИмени.length < 0.6) continue;

    const минор = ценаВКопейки(цена[1]);
    if (минор !== undefined && минор > 0) return true;
  }
  return false;
}

/**
 * Решение по приметам страницы, когда разметке верить нельзя.
 *
 * Вынесено отдельно, потому что сюда приходят из двух разных мест: и когда
 * разметки нет вовсе, и когда она есть, но не читается. Держать эти рассуждения
 * в двух копиях — верный способ развести их со временем.
 */
function общиеПриметы(doc: Document, признакиСтраницы: ПризнакиТовара): boolean {
  if (различныхЦенВТексте(doc) >= 15) return false;

  /*
   * У карточки в адресе есть опознавательный кусок, у раздела — короткое слово.
   *
   * Замер по корпусу: у всех двадцати трёх настоящих карточек последний кусок
   * адреса либо длинный, либо с номером — «pantenolsprej-463-aerozol...»,
   * «3139136», «zr2608012505-60». У разделов там «baletki», «umbrellas»,
   * «main-dish». Так и должно быть: товару нужен уникальный опознаватель,
   * а разделу хватает слова.
   *
   * Применяем только здесь, где мы и так гадаем по приметам. В ветке, где
   * магазин сам разметил товар с ценой, адрес перевешивать разметку не должен:
   * ошибка стоила бы потерянной карточки, а это дороже лишнего раздела.
   */
  if (!адресПохожНаТовар(doc)) return false;

  /*
   * Без разметки цена обязательна.
   *
   * Раньше она была одним голосом из четырёх, и двух других хватало, чтобы
   * объявить карточкой пустой раздел «Садовые измельчители» у X-Com: крошки
   * глубокие, кнопка «купить» в шапке — вот и два голоса. В вишлист уезжало
   * желание с названием «Главная» и без цены.
   *
   * Когда магазин ничего не разметил, единственное надёжное свидетельство
   * того, что здесь продают одну вещь, — это её цена. Карточку без цены и без
   * разметки мы пропустим, но взамен не будем звать карточкой что попало:
   * желание без цены и с чужим названием человеку не нужно, а чинить его
   * руками дольше, чем завести заново.
   */
  if (!признакиСтраницы.ценаИЗаголовок) return false;

  /*
   * Второго признака не требуем, но верим странице, когда она сама говорит,
   * что она не про товар.
   *
   * Требовать вдобавок крошки или кнопку заманчиво: на статье про выбор
   * чайника цена тоже встречается. Но у настоящих карточек эти признаки есть
   * не всегда, а цена почти всегда, и строгое правило било бы по магазинам
   * с непривычной вёрсткой — то есть ровно по тем, ради которых всё
   * и затевалось. Статью же выдаёт её собственная метка.
   */
  /*
   * Метка «статья» — довод против, но не приговор.
   *
   * Соблазн отвергать такие страницы сразу разбился о Bosco: карточка духов
   * объявляет себя статьёй, и это не редкость у магазинов на готовых движках.
   * Поэтому при такой метке просим ещё одно подтверждение — кнопку покупки,
   * глубокие крошки или прямое объявление товара. У настоящей статьи про выбор
   * кроссовок ничего этого нет.
   */
  /*
   * Подтверждать метку статьи крошками оказалось нельзя.
   *
   * Крошки отвечают не на тот вопрос. Они говорят, где страница лежит в дереве
   * сайта, — а спрашиваем мы, продаётся ли на ней вещь. У статьи внутри
   * раздела крошки такие же глубокие, как у товара: поймано на Tiu, где
   * страница акции букмекера «Приветственный бонус до 25 000 ₽» набирала
   * четыре уровня крошек и уезжала в вишлист как товар за 25 000 рублей.
   *
   * Осталось два подтверждения, и оба про покупку: кнопка и прямое объявление
   * товара. Ни одна карточка корпуса на крошки не опиралась — карточка духов
   * Bosco, ради которой сделана эта уступка метке статьи, держится кнопкой
   * «В корзину».
   */
  if (/article|blog|profile|video/i.test(признакиСтраницы.типСтраницы)) {
    return признакиСтраницы.кнопкаПокупки || признакиСтраницы.меткаТовара;
  }
  return true;
}


/**
 * Стоит ли переспросить, прежде чем добавлять.
 *
 * Расширение показывает кнопку только на карточке товара, а закладку человек
 * нажимает сам и где угодно — в том числе в каталоге или на главной магазина.
 * Оттуда уезжает желание с названием раздела, и человек обнаруживает это
 * позже, разбирая список.
 *
 * Молча отказывать нельзя: он нажал осознанно, и бывают страницы, которых
 * наши признаки не знают. Поэтому не запрет, а вопрос.
 */
/**
 * Страница не похожа на карточку товара — значит человеку стоит сказать.
 *
 * Раньше называлась «похоже_на_ошибку_страницы», и это имя врало. Оно
 * обещает проверку страницы ошибки, а внутри — полное распознавание
 * карточки: витрина, статья, раздел, заслон. Я на этом имени сам споткнулся
 * и полез было чинить закладку, решив, что до неё дневная работа
 * не доезжает. Доезжает: имя отставало от содержимого.
 *
 * В этом файле такое уже дорого обходилось — там, где пояснение говорило
 * одно, а код делал другое. Поэтому имя приведено в соответствие.
 */
export function страницаНеПохожаНаТовар(doc: Document): boolean {
  /*
   * Витрина закрывает вопрос сразу, и запасное условие её не отменяет.
   *
   * Это уже вторая моя ошибка одного рода: точно так же в расширении запасное
   * «нашлись название и цена» перебивало вердикт «это витрина». На каталоге
   * название и цена находятся прекрасно — по первой же плитке. Порядок
   * проверок здесь не стилистика, а суть.
   */
  if (товаровВРазметке(doc) > 1) return true;

  /*
   * Решает опознаватель, и запасного мнения после него нет.
   *
   * Здесь стояло «разметки нет, но нашлись название и цена — значит всё
   * в порядке», и это третье по счёту место с одной и той же конструкцией:
   * слабое условие, поставленное после вердикта и потому его отменяющее.
   * На витрине без разметки название и цена находятся по первой же плитке,
   * и предупреждение, ради которого всё затевалось, не показывалось.
   *
   * Здесь мы ничего не запрещаем, а только предупреждаем: добавить карточку
   * человек всё равно сможет. Значит цена ошибки несимметрична, и сомневаться
   * лучше в свою сторону.
   */
  return !этоКарточкаТовара(doc);
}
